{
  "app": {
    "title": "Lodestar",
    "subtitle": "Find where you'll thrive — relocation atlas"
  },
  "brand": {
    "tagline": "Relocation Atlas"
  },
  "home": {
    "meta": {
      "title": "Lodestar — find the country you'd thrive in",
      "description": "A measured atlas for relocation. Nine-question quiz scores 31 countries on climate, cost, tax, visa, healthcare, language, safety, jobs, family, banking. Open data. No signup."
    },
    "header": {
      "atlas": "Atlas",
      "guide": "Guide",
      "howTo": "How-to",
      "map": "Map",
      "methodology": "Methodology"
    },
    "hero": {
      "title": "Find where you'll actually thrive.",
      "sub": "Stop cycling through the same five countries. Nine questions, 31 countries, real data — you'll know which ones actually fit your life.",
      "tagline": "Thirty countries. Ten dimensions of life. Two minutes.",
      "italyOnly": "Detailed comune-level map: Italy only · Quiz: all 31 countries",
      "primaryCta": "Find my match",
      "secondaryCta": "Or browse all 39 countries",
      "preview": {
        "label": "Sample ranking — answers cycling",
        "caption": "Based on {{preset}}",
        "tryYours": "Try yours →"
      },
      "presets": {
        "alpine_eu": "alpine climate, EU passport, low tax",
        "tropical_dn": "tropical climate, digital nomad",
        "med_coast": "Mediterranean coast, family",
        "northern_jobs": "northern Europe, jobs",
        "sun_cost": "sun, cost-conscious",
        "city_family": "big city, family priorities"
      }
    },
    "trust": {
      "label": "Sourced from"
    },
    "taste": {
      "title": "A taste, no signup.",
      "sub": "Pick what matters most to you. We'll show the top three countries instantly.",
      "question": "What matters most?",
      "basedOn": "Based on {{dim}}",
      "takeFull": "Take the full quiz to see 31 countries ranked on your full priorities →",
      "empty": "Select a dimension above to preview the top three.",
      "dim": {
        "climate": "Climate",
        "cost": "Cost",
        "visa": "Visa",
        "healthcare": "Healthcare",
        "safety": "Safety",
        "tax": "Tax"
      }
    },
    "how": {
      "title": "How it works",
      "s1": {
        "n": "01",
        "title": "Know your non-negotiables",
        "body": "Climate feel, budget, passport, family — and what you'd refuse to give up. Nine questions, two minutes."
      },
      "s2": {
        "n": "02",
        "title": "Hard limits cut first, scores second",
        "body": "Countries that can't work — wrong visa path, incompatible climate, no banking access — are excluded entirely, not just marked down."
      },
      "s3": {
        "n": "03",
        "title": "Walk away with a clear direction",
        "body": "Top countries with match scores and plain-language reasons. Explore the Italy map when you're ready to go deeper."
      }
    },
    "pillars": {
      "title": "Why people are relocating right now",
      "subtitle": "Most moves don't come from one reason. They come from two or three overlapping ones. The quiz figures out which weigh heaviest for you.",
      "tax": {
        "title": "Tax & finance",
        "body": "Tax-friendly residency, special expat regimes, worldwide-income optimisation. Where your money actually grows, not just where it lives.",
        "link": "See Portugal's NHR successor"
      },
      "career": {
        "title": "Career & employment",
        "body": "Career growth, sponsored employment, skilled-worker pathways. Where to build the next 5–10 years professionally, not just the next 12 months of lifestyle.",
        "link": "Preview what's coming"
      },
      "family": {
        "title": "Family & lifestyle",
        "body": "Schools, healthcare, daily rhythm. The shape of the life you'd actually be moving toward, not just away from.",
        "link": "Explore Italy"
      },
      "independence": {
        "title": "Independence",
        "body": "Remote work, freelance freedom, financial independence. For when your income isn't tied to a place — but you'd still rather live somewhere worth living.",
        "link": "Explore the UAE"
      },
      "bridge": "Pick the pillar that matters most, or take the quiz and we'll figure out the weights."
    },
    "dims": {
      "title": "Ten dimensions of life.",
      "sub": "Each scored 0–10 from open data.",
      "card": {
        "climate": {
          "title": "Climate",
          "source": "Open-Meteo · 1991–2020"
        },
        "cost": {
          "title": "Cost",
          "source": "livingcost.org · 2026"
        },
        "tax": {
          "title": "Income tax",
          "source": "OECD · 2024"
        },
        "property_tax": {
          "title": "Property tax",
          "source": "Lodestar KB · 2026-Q2",
          "range": "annual + purchase + capital-gains rolled into a tier"
        },
        "visa": {
          "title": "Visa",
          "source": "Gov. portals · 2024"
        },
        "healthcare": {
          "title": "Healthcare",
          "source": "WHO UHC · 2023"
        },
        "safety": {
          "title": "Safety",
          "source": "IEP GPI · 2026"
        },
        "language": {
          "title": "Language",
          "source": "EF EPI · 2025"
        },
        "jobs": {
          "title": "Jobs / internet",
          "source": "Ookla · 2024"
        },
        "family": {
          "title": "Family",
          "source": "UNESCO + IB · 2024"
        },
        "banking": {
          "title": "Banking",
          "source": "Wise · FATF · 2024"
        }
      },
      "rangeExample": "e.g. {{high}} {{highScore}} · {{low}} {{lowScore}}"
    },
    "atlas": {
      "title": "Thirty countries. Five regions. Four tiers of data depth.",
      "tierT0": "T0 — Italy is the pioneer. Granular comune-level data, 8000 places, live map.",
      "tierT1": "T1 — Top destinations. Country-level live metrics. Maps shipping over the next quarters.",
      "tierT2T3": "T2 / T3 — Twenty-three more. Notify when a country goes live.",
      "browseAll": "Browse all 39 countries →",
      "guideCta": "How to use the map →",
      "guideBadge": "Guide →"
    },
    "map": {
      "eyebrow": "Interactive Map",
      "title": "Drill into any country — place by place",
      "sub": "Once you have a country shortlist, zoom in: filter by climate, cost, healthcare, safety. Pin places, compare them side-by-side, ask AI a follow-up. Currently available for Italy — more countries shipping next.",
      "screenshotAlt": "Interactive map with filters and commune-level data — Italy example",
      "cta": "Try it on Italy →",
      "ctaWorld": "Open the world map",
      "feat": {
        "filters": {
          "title": "Layer by layer",
          "body": "Climate normals, cost of living, healthcare, internet — switch data layers to find what matters to you."
        },
        "details": {
          "title": "Place profiles",
          "body": "Click any dot for full stats, AI-generated context, and nearby alternatives worth considering."
        },
        "compare": {
          "title": "Shortlist & compare",
          "body": "Save anywhere to your shortlist and compare side-by-side before committing to a visit."
        }
      }
    },
    "method": {
      "title": "A note on method —",
      "body1": "Every country is scored from open data: climate normals 1991–2020 from Open-Meteo, tax statutes from OECD, language proficiency from EF EPI, safety from the Institute for Economics & Peace.",
      "body2": "We weight them geometrically — a single weak dimension can't be balanced out by a strong one. Hard deal-breakers (no Schengen, no banking access for your passport) exclude rather than penalize.",
      "body3": "The match score is our model talking, not a verdict. Use the map to look closer.",
      "link": "Read the full methodology →",
      "propertyTaxLabel": "New: property-tax dataset",
      "propertyTaxBody": "Beyond income tax, every country card now carries a verified property-tax tier (annual ownership tax + purchase transaction + capital gains + wealth tax). Sourced from OECD, EU Taxes in Europe Database, Global Property Guide and KPMG. Half-life is ~12 months — tax codes change at fiscal year start, so we re-verify quarterly."
    },
    "faq": {
      "title": "Questions",
      "q1": "Who is this for?",
      "a1": "Anyone seriously considering an international move — remote workers, families, retirees, founders. You know you want to leave; you just haven't found where you'd actually feel at home.",
      "q2": "How is this different from just asking ChatGPT?",
      "a2": "ChatGPT gives you an opinion. We give you a score from open data: climate normals, OECD tax tables, WHO healthcare index, Ookla internet speeds. Every number has a source. The quiz learns your trade-offs and ranks accordingly — no generic 'Portugal is great' advice.",
      "q3": "How do you score countries?",
      "a3": "Ten dimensions (climate, cost, tax, visa, healthcare, safety, language, internet, family, banking), each scored 0–10 from open data. We combine them geometrically, meaning one bad dimension really matters — it can't be averaged away. Hard deal-breakers exclude a country entirely.",
      "q4": "Will more countries get a detailed map like Italy?",
      "a4": "Yes. Italy is our proof of concept — 8,000 places, comune-level climate, prices, internet, demographics. Portugal, Spain, and UAE are next. Subscribe in the atlas to be notified per country.",
      "q5": "Is the data current?",
      "a5": "Climate normals are 1991–2020 (the WMO 30-year standard — the most meaningful baseline). Tax, visa, and language data refreshes annually. Every number shows its source and year inline — nothing is hidden."
    },
    "footer": {
      "ctaTitle1": "You already know you want to move.",
      "ctaTitle2": "Let's find out where.",
      "ctaPrimary": "Find my match",
      "ctaSecondary": "Or browse all 39 countries →",
      "rights": "© 2026 Lodestar",
      "privacy": "Privacy",
      "terms": "Terms",
      "partners": "For consultants",
      "github": "GitHub",
      "tagline": "International relocation guidance across 39 countries — quiz, atlas, journalistic country guides.",
      "guidesTitle": "Country guides",
      "checklist": "Checklist",
      "allCountries": "All 39 countries →",
      "exploreTitle": "Explore",
      "quiz": "Take the quiz",
      "topics": "Browse topics",
      "guideLink": "How to use the site",
      "worldMap": "World map",
      "countryMap": "{{country}} interactive map",
      "newsletter": {
        "label": "Monthly digest",
        "copy": "One thoughtful email a month — new countries we cover, data updates, relocation reads. No spam, unsubscribe in one click.",
        "placeholder": "you@example.com",
        "submit": "Subscribe",
        "sending": "Sending…",
        "confirm": "A confirmation email is on its way — nothing arrives until you click the button in it.",
        "failed": "Sign-up did not go through — your address was not saved. Try again."
      },
      "refund": "Refund policy",
      "report": "Relocation report"
    }
  },
  "worldMap": {
    "meta": {
      "title": "World map — Lodestar relocation atlas",
      "description": "30 relocation destinations on one map. Open the interactive Italy or Spain map, read full country guides (Italy, Portugal, Spain, UAE), or browse the rest of the atlas. Click any coloured country to dive in."
    },
    "eyebrow": "Quiet Atlas world view",
    "title": "Thirty destinations on one map.",
    "lede": "Every country we cover, coloured by what's ready to read. Click Italy or Spain to open the interactive municipality-level map. Click Portugal or the UAE for the full country guide. Click any other coloured country to jump into the atlas.",
    "regionAria": "World map of relocation destinations",
    "attribution": "Boundaries: Natural Earth via world-atlas TopoJSON. Drawn for navigation, not for diplomatic claims.",
    "legend": {
      "aria": "Map legend",
      "title": "Status legend",
      "liveMap": "Live interactive map",
      "fullGuide": "Full country guide",
      "inRoster": "In the atlas (guide shipping)",
      "notInRoster": "Not in our roster",
      "tip": "Click any coloured country"
    },
    "popup": {
      "liveMap": "Interactive map + full guide",
      "fullGuide": "Full country guide ready",
      "inRoster": "In the atlas — full guide shipping next",
      "openMap": "Open interactive map →",
      "readGuide": "Read the guide",
      "openAtlas": "Open the atlas →"
    },
    "strip": {
      "liveMap": {
        "label": "Live interactive map",
        "body": "Click into thousands of municipalities: climate, prices, internet, demographics.",
        "ctaCountry": "Open the {{country}} map →"
      },
      "guides": {
        "label": "Full country guides",
        "title": "Italy, Portugal, Spain, UAE",
        "body": "Hub + 12 chapters per country, in EN and RU. Journalistic, not a wiki dump.",
        "cta": "Browse guides →"
      },
      "roster": {
        "label": "In the atlas — guides next",
        "titleSuffix": "more countries",
        "body": "Already in the quiz + the country catalogue. Full guide shipping in the next waves.",
        "cta": "See the atlas →"
      },
      "quiz": {
        "label": "Not sure where to start?",
        "title": "Take the 2-min quiz",
        "body": "Nine questions, all 31 countries scored, ranked match. Then walk back into the map.",
        "cta": "Start the quiz →"
      }
    }
  },
  "quiz": {
    "meta": {
      "title": "Relocation quiz — match yourself to 39 countries",
      "description": "Answer ten questions about climate, budget, visas and family, then get a ranked shortlist of countries scored on ten dimensions of life. Free, no signup."
    },
    "landing": {
      "title": "Where will you actually thrive?",
      "subtitle": "Tell us how you want to live. We'll match you to the right country in two minutes.",
      "tagline": "Thirty countries, scored on ten dimensions of life.",
      "startCta": "Find my match",
      "skipCta": "Open Italy map",
      "comingSoon": "Coming soon",
      "samplesLabel": "Three places, demonstrated"
    },
    "q": {
      "horizon": {
        "label": "When are you planning to move?",
        "opt": {
          "now": "This year — actively planning",
          "soon": "In 2–5 years",
          "next_year": "In 1–2 years",
          "explore": "Just exploring, no timeline",
          "already": "Already relocated"
        }
      },
      "passport": {
        "label": "What passport(s) do you hold?",
        "description": "Used to flag visa & banking pathways that differ for post-Soviet passport holders. Russia / Belarus / Ukraine are listed separately because — since 2022 — host countries treat each very differently in banking, property purchases, and document issuance.",
        "opt": {
          "eu": "EU member state",
          "anglo": "US / UK / Australia / Canada / NZ",
          "ru": "Russian Federation passport",
          "by": "Belarusian passport",
          "ua": "Ukrainian passport",
          "other": "Other (Asia, LATAM, Africa, Middle East)"
        }
      },
      "visa_pathway": {
        "label": "Which visa paths apply to you? (multi-select)",
        "description": "Optional. Pick all that could realistically work for you. We'll boost countries where these pathways exist and quietly down-rank countries where none of your pathways are available.",
        "opt": {
          "digital_nomad": "Digital nomad / remote worker visa",
          "freelance_self_employed": "Freelance / self-employed permit",
          "golden_investor": "Investor visa (usually €250k+)",
          "family_reunion": "Family reunion (spouse, parent, child)",
          "ancestry": "Citizenship by descent / ancestry",
          "work_sponsor": "Work permit from an employer",
          "student_to_perm": "Study, then convert to residency",
          "refugee_protection": "Refugee / asylum / temporary protection",
          "unknown_exploring": "Don't know yet — exploring options"
        }
      },
      "climate": {
        "label": "Which climate feels right?",
        "opt": {
          "alps": "Alps / mountains",
          "mediterranean": "Mediterranean",
          "continental": "Continental",
          "tropical": "Warm coast / tropical",
          "northern": "Northern / cool",
          "any": "No preference"
        }
      },
      "urban_density": {
        "label": "Where would you ideally live?",
        "description": "Where you settle shapes your cost of living, job options, and how much local language you'll need day to day.",
        "opt": {
          "capital": "Capital or major city (1M+)",
          "mid_city": "Mid-size city (100k–500k)",
          "town": "Small town or village (under 50k)",
          "coastal": "Coastal area (any size)"
        },
        "help": {
          "capital": "Like Berlin or Madrid — the most jobs and the easiest with English, best transport and services, but rent runs ~30% above the national average.",
          "mid_city": "Like Porto or Granada — full city amenities and a small international community, noticeably cheaper than the capital. The sweet spot for most.",
          "town": "Quietest and cheapest to live in, with a tight local community — but you'll lean on the local language and travel for big-city jobs, flights, and specialist services.",
          "coastal": "Sea within reach at any size — milder climate, a slower summer-tourist rhythm. Like Valencia or Nice; a sea view usually adds a rent premium."
        }
      },
      "budget": {
        "label": "Monthly rent budget",
        "description": "Approximate rent for a family-size apartment (2-3 bedrooms) in your target city, in EUR per month. Utilities and groceries are not counted. Skip if you'd rather not say."
      },
      "priorities": {
        "label": "What matters most in everyday life? (up to 3)",
        "opt": {
          "safety": "Safety",
          "healthcare": "Healthcare",
          "schools": "Schools",
          "transport": "Public transport",
          "nature": "Nature & outdoors",
          "ru-community": "Russian-speaking diaspora nearby",
          "mountain_sports": "Skiing / mountain sports",
          "water_sports": "Water sports / coast"
        }
      },
      "income": {
        "label": "Where does your income come from?",
        "opt": {
          "remote": "Remote work",
          "local": "Local job",
          "business": "Own business",
          "passive": "Passive income (dividends, rent, pension)"
        }
      },
      "income_amount": {
        "label": "Roughly how much per month?",
        "description": "Optional. Most digital-nomad visas require €2.5–3k/mo proof of income — without this we can't tell you why DN-friendly countries might still be out of reach.",
        "opt": {
          "lt1500": "Under €1,500 / month",
          "1500to3000": "€1,500 – €3,000",
          "3000to6000": "€3,000 – €6,000",
          "gt6000": "Over €6,000"
        }
      },
      "tax_residency_intent": {
        "label": "What's your plan for taxes after the move?",
        "description": "Optional. Where you pay taxes changes which countries actually save you money. Not sure yet — pick the last option and we'll still show you what fits.",
        "opt": {
          "break_origin": "Leave my home country's tax system completely",
          "keep_origin": "Keep paying taxes in my home country (e.g. US citizen)",
          "dual": "Not sure — might stay tied to two countries",
          "treaty_minimize": "Use a tax treaty to avoid paying double",
          "unsure": "Haven't thought about this yet"
        }
      },
      "career_intent": {
        "label": "Will you need to earn an income locally after you move?",
        "description": "Optional. Most people relocate for life reasons first and sort out income later — this just tells us whether to weave the local {{term:labour-market}} and a work consultation into your plan.",
        "opt": {
          "primary": "Yes — I'll look for local work",
          "eventual": "Maybe later, not at first",
          "none": "No — my income comes with me"
        },
        "help": {
          "primary": "Finding work locally is central to your move. We'll weigh each country's job market and how much of it runs in English — and when you're ready, we can connect you with a relocation-and-work consultant.",
          "eventual": "You're moving for other reasons but may job-search within a year or two. We'll show the job market as context, not as a filter.",
          "none": "Remote, freelance, savings, pension, or family-supported. The local job market won't drive your decision."
        }
      },
      "family": {
        "label": "Who is relocating with you?",
        "description": "Select everyone moving with you. Affects priorities like schools, paediatric care, pet import rules.",
        "opt": {
          "solo": "Just me",
          "partner": "Spouse or partner",
          "kids-preschool": "Kids under 7 (preschool)",
          "kids-school": "Kids 7-17 (school age)",
          "pets": "Pets (cat / dog)"
        }
      },
      "languages": {
        "label": "Languages you speak or are willing to learn",
        "opt": {
          "en": "English",
          "it": "Italian",
          "es": "Spanish",
          "de": "German",
          "fr": "French",
          "pt": "Portuguese",
          "any": "Open to any",
          "ru-only": "Only my native language (no English/local)"
        }
      },
      "dealbreakers": {
        "label": "Any deal-breakers?",
        "description": "Optional. Pick what you absolutely want to avoid.",
        "opt": {
          "hot-summer": "Hot summers (over 35C)",
          "snow": "Snowy winters",
          "humidity": "High humidity",
          "earthquakes": "Earthquakes",
          "complex-visa": "Complex visa process",
          "expensive-healthcare": "Expensive healthcare"
        }
      },
      "buy_property": {
        "label": "Planning to buy property there?",
        "description": "Optional. Buying activates a second tax queue (annual + purchase + capital gains) that changes which countries fit your budget.",
        "opt": {
          "yes": "Yes — buying within a few years",
          "no": "No — renting only",
          "unsure": "Not sure yet"
        }
      },
      "ties": {
        "label": "Which of these countries do you already have a connection to?",
        "description": "Family, friends, lived there before, speak the language, visit often — any meaningful prior tie. Pick all that apply. Skip if you're starting completely fresh.",
        "opt": {
          "AE": "🇦🇪 UAE",
          "AL": "🇦🇱 Albania",
          "AR": "🇦🇷 Argentina",
          "BR": "🇧🇷 Brazil",
          "CA": "🇨🇦 Canada",
          "CL": "🇨🇱 Chile",
          "CO": "🇨🇴 Colombia",
          "CR": "🇨🇷 Costa Rica",
          "CY": "🇨🇾 Cyprus",
          "CZ": "🇨🇿 Czechia",
          "DE": "🇩🇪 Germany",
          "EE": "🇪🇪 Estonia",
          "ES": "🇪🇸 Spain",
          "FR": "🇫🇷 France",
          "GB": "🇬🇧 United Kingdom",
          "GE": "🇬🇪 Georgia",
          "GR": "🇬🇷 Greece",
          "HR": "🇭🇷 Croatia",
          "HU": "🇭🇺 Hungary",
          "ID": "🇮🇩 Indonesia",
          "IE": "🇮🇪 Ireland",
          "IT": "🇮🇹 Italy",
          "ME": "🇲🇪 Montenegro",
          "MT": "🇲🇹 Malta",
          "MU": "🇲🇺 Mauritius",
          "MX": "🇲🇽 Mexico",
          "MY": "🇲🇾 Malaysia",
          "NL": "🇳🇱 Netherlands",
          "PA": "🇵🇦 Panama",
          "PL": "🇵🇱 Poland",
          "PT": "🇵🇹 Portugal",
          "PY": "🇵🇾 Paraguay",
          "RS": "🇷🇸 Serbia",
          "SG": "🇸🇬 Singapore",
          "TH": "🇹🇭 Thailand",
          "TR": "🇹🇷 Turkey",
          "UY": "🇺🇾 Uruguay",
          "VN": "🇻🇳 Vietnam",
          "ZA": "🇿🇦 South Africa"
        }
      },
      "family_children_count": {
        "label": "How many children?",
        "description": "So the cost figures further on reflect your actual household, not a generic average family.",
        "opt": {
          "1": "1 child",
          "2": "2 children",
          "3plus": "3 or more"
        }
      }
    },
    "budget": {
      "bucket": {
        "lt500": "Under EUR 500",
        "500to1000": "EUR 500-1000",
        "1000to2000": "EUR 1000-2000",
        "gt2000": "EUR 2000+"
      },
      "skip": "Prefer not to say"
    },
    "nav": {
      "back": "Back",
      "next": "Next",
      "skip": "Skip",
      "finish": "See results"
    },
    "hint": {
      "selectUpTo": "Select up to {{n}}",
      "selectAll": "Select all that apply"
    },
    "progress": {
      "stepOf": "Step {{current}} of {{total}}",
      "timeLeft": "~{{minutes}} min left",
      "timeLeftSoon": "~1 min left",
      "timeLeftEnd": "Almost done",
      "topic": {
        "horizon": "Horizon",
        "passport": "Passport",
        "visa_pathway": "Visa path",
        "climate": "Climate",
        "urban_density": "Where",
        "budget": "Budget",
        "priorities": "Priorities",
        "income": "Income",
        "income_amount": "Income amount",
        "tax_residency_intent": "Tax residency",
        "career_intent": "Work",
        "family": "Family",
        "languages": "Languages",
        "dealbreakers": "Dealbreakers",
        "ties": "Existing ties",
        "buy_property": "Buy property?"
      }
    },
    "interstitial": {
      "title": "Halfway peek",
      "subtitle": "Based on your {{answered}} answers so far, these three countries lead. A few more questions will refine and re-rank.",
      "subtitleNone": "Pick a few more answers and your top matches will appear here.",
      "rankPrefix": "#",
      "continue": "Continue"
    },
    "loading": {
      "title": "Analyzing 31 countries by 47 parameters…",
      "subtitle": "Building your personal ranking"
    },
    "result": {
      "matchPercent": "{{percent}}% match",
      "blockerLabel": "Consider:",
      "comingSoon": "Map coming soon",
      "title": "Your top matches",
      "subtitle": "Based on {{answersCount}} answers · {{dimensions}} dimensions · {{countriesCount}} countries evaluated",
      "subtitleWithMap": "Based on {{answersCount}} answers — your filters are pre-loaded in the {{name}} map.",
      "matchLabel": "Match",
      "rank": "#{{rank}} of {{total}} countries",
      "whyFits": "Why this fits",
      "consider": "Consider",
      "sources": "Sources: ISTAT · 2024 · Open-Meteo · 1991-2020 · OECD · 2024",
      "openMap": "Open {{country}} map →",
      "notify": "Notify me when ready",
      "notifyContext": "Interactive regional map · in development. We'll also send a one-time sign-in link so you can access your saved quiz from any device.",
      "notifyPlaceholder": "you@example.com",
      "notifySubmit": "Notify me",
      "notifySuccess": "Got it — check your inbox for a sign-in link, then we'll email you when {{country}} is ready.",
      "nextSteps": "Moving checklist & next steps",
      "retake": "Retake quiz",
      "share": "Share",
      "shareCopied": "Link copied",
      "excluded": "Excluded ({{count}})",
      "unscored": "Coming soon ({{count}})",
      "expand": "Show breakdown",
      "collapse": "Hide breakdown",
      "noMatches": "No matches passed the hard filters. Try removing some deal-breakers.",
      "scoreBreakdownAria": "Score breakdown",
      "componentTooltip": "{{dim}}: {{component}} × weight {{weight}}",
      "dim": {
        "climate": "Climate",
        "cost": "Cost",
        "tax": "Tax",
        "visa": "Visa",
        "healthcare": "Healthcare",
        "safety": "Safety",
        "language": "Language",
        "jobs": "Jobs",
        "family": "Family",
        "banking": "Banking"
      },
      "reason": {
        "climate": "Climate matches your preferences",
        "cost": "Budget covers this country",
        "tax": "Friendly tax regime",
        "visa": "Easy visa pathway",
        "healthcare": "Strong universal healthcare",
        "safety": "High safety rating",
        "language": "Language barrier is low",
        "jobs": "Good internet and remote-work fit",
        "family": "Family-friendly schools",
        "banking": "Banking is workable for your passport"
      },
      "seeRegions": "See top regions for your profile",
      "openFullMap": "Or browse the full map",
      "personaWhyFits": "Why this {{unit}} fits you",
      "passportNotice": {
        "label": {
          "cautious": "Heads up for your passport",
          "restrictive": "Significant friction for your passport",
          "hostile": "Country effectively closed for your passport"
        }
      },
      "blocker": {
        "climate": "Climate may not match your preferences",
        "cost": "Cost of living strains your budget",
        "tax": "Tax regime is heavy",
        "visa": "Visa pathway is complex",
        "healthcare": "Healthcare quality is below average",
        "safety": "Safety rating is lower than peers",
        "language": "Language barrier is high",
        "jobs": "Internet/jobs market is limited",
        "family": "Family infrastructure is weaker",
        "banking": "Banking is restricted for your passport",
        "detail": {
          "languageRuOnly": "you picked Russian only — local language (B1+) needed for long-term residency",
          "languageEnOnly": "you picked English only — basic local language usually expected for permits",
          "visaCis": "Post-Soviet passport (RU/BY/UA): most paths require a sponsor or investor route",
          "visaOther": "passport tier limits visa-free options — sponsor route typically needed",
          "costLowBudget": "your stated budget is in the lower bucket (≤ €1000/mo)",
          "taxRemote": "you flagged remote/business income — local tax residency adds friction",
          "bankingCis": "Post-Soviet passport (RU/BY/UA): account opening usually blocked by sanctions screening",
          "healthcarePrio": "you marked healthcare as a top priority",
          "familyKids": "you flagged school-age kids — local language & school slots matter",
          "climateDealbreaker": "this country triggers a dealbreaker you picked"
        },
        "withContext": "{{generic}} — {{detail}}",
        "humidity_above_70": "High humidity year-round",
        "seismic_country": "Located in a seismic zone",
        "warm_summer": "Warm summers (30-35°C)",
        "low_english_for_ru_only": "Low English level for Russian-only speakers",
        "moderate_english_for_ru_only": "Moderate English level for Russian-only speakers",
        "banking_closed_for_ru": "Banking is closed or restricted for RU passport",
        "no_digital_nomad_or_freelance_path": "No digital nomad or freelance visa",
        "healthcare_below_70": "Healthcare quality below threshold",
        "summer_above_35c": "Summers above 35°C",
        "winter_below_0c": "Winters below 0°C",
        "cost_exceeds_low_budget": "Cost exceeds your budget",
        "passport_hostile_ru": "Country effectively closed for RU passport (banking + notary refusals)",
        "passport_hostile_by": "Country effectively closed for BY passport (banking + notary refusals)",
        "passport_hostile_ua": "Country effectively closed for UA passport",
        "passport_restrictive_ru": "Significant friction for RU passport (banking + paperwork)",
        "passport_restrictive_by": "Significant friction for BY passport (banking + paperwork)",
        "passport_restrictive_ua": "Significant friction for UA passport",
        "passport_cautious_ru": "Some passport friction (extra screening for RU)",
        "passport_cautious_by": "Some passport friction (extra screening for BY)",
        "passport_cautious_ua": "Some passport friction (extra screening for UA)",
        "partial_snow_risk": "Some regions have snowy winters",
        "fallback": "Excluded by your filters"
      },
      "adjust": {
        "title": "Want to adjust priorities?",
        "description": "Tweak how much each dimension matters and the rankings update live.",
        "reset": "Reset to defaults",
        "sliderAria": "{{dim}} weight: {{value}}",
        "apply": "Apply",
        "pending": "Pending changes — click Apply to re-rank.",
        "synced": "Ranking is up to date."
      },
      "allRanked": "{{count}} more scored countries",
      "climate": {
        "julLabel": "Jul max",
        "janLabel": "Jan min",
        "rangeNote": "(by region)"
      },
      "heroTitle": "{{flag}} {{name}} is your top match.",
      "heroTitleGeneric": "Your top matches are ready.",
      "heroTitlePlural": "Your top matches are ready.",
      "persona": {
        "title": "What you asked for",
        "aria": "Your quiz inputs"
      },
      "podium": {
        "title": "Your top 3",
        "aria": "Top three matches at a glance"
      },
      "propertyTax": {
        "label": "Property tax"
      }
    },
    "scenarios": {
      "saveButton": "Save this ranking",
      "savePlaceholder": "e.g. Nomad lifestyle",
      "saveConfirm": "Save",
      "saveCancel": "Cancel",
      "savedAs": "Saved as \"{{name}}\"",
      "sectionTitle": "Your saved rankings",
      "load": "Load",
      "delete": "Delete",
      "savedOn": "Saved {{date}}",
      "topMatch": "Top: {{flag}} {{name}} · {{percent}}%",
      "compare": "Compare",
      "compareClose": "List",
      "atLimit": "{{count}}/{{max}} saved — next save replaces the oldest"
    },
    "emailGate": {
      "label": "Your results are in",
      "headline": "{{flag}} {{name}}",
      "matchLine": "{{percent}}% match",
      "restLabel": "+{{count}} more countries in your ranking",
      "formTitle": "Save and unlock the full ranking",
      "formSub": "We'll send a one-time link so you can access your ranking from any device, plus updates when your top countries get a detailed map.",
      "placeholder": "your@email.com",
      "cta": "Unlock ranking →",
      "sending": "Saving…",
      "failed": "Not saved — the link could not be sent. Try again, or skip below to see the full ranking.",
      "privacy": "No spam, no password. Unsubscribe any time.",
      "skip": "Skip — show me anyway"
    },
    "echo": {
      "horizon": {
        "now": "Moving this year — visa timelines will filter the list.",
        "soon": "2–5 year window — plenty of time to research well.",
        "explore": "Just exploring — we'll show you the full picture.",
        "already": "Already relocated — we'll help you validate the choice."
      },
      "passport": {
        "eu": "EU passport — most countries are fully accessible.",
        "anglo": "Strong travel document — minor banking friction in a few places.",
        "ru": "Russian passport — we'll mark countries where banking, property purchase, or document issuance is currently blocked.",
        "by": "Belarusian passport — many countries apply Russia-equivalent rules; we'll mark restrictions per destination.",
        "ua": "Ukrainian passport — most of the EU/EEA is welcoming via Temporary Protection through 2026.",
        "other": "Focusing on countries with the right visa paths for you."
      },
      "climate": {
        "alps": "Alpine preference noted — four seasons, cool dry summers.",
        "mediterranean": "Mediterranean — warm, dry, sun-rich.",
        "continental": "Continental — four seasons, moderate everything.",
        "tropical": "Warm coast — heat and sun as features, not bugs.",
        "northern": "Northern cool — long winters, long summer evenings.",
        "any": "Climate open — we'll rank by everything else.",
        "_multi": "Two climate types noted — we'll find where both overlap."
      },
      "urban_density": {
        "capital": "Capital city — best jobs, most English, higher costs.",
        "mid_city": "Mid-size city — the sweet spot for most people.",
        "town": "Small town — quieter, cheaper, local language matters more.",
        "coastal": "Coastal — sea air and mild humidity in the mix.",
        "_multi": "Two location types noted — flexible on the mix."
      },
      "priorities": {
        "safety": "Safety weighted — filtering for low crime and stable rule of law.",
        "healthcare": "Healthcare weighted — public system quality moves up.",
        "schools": "Schools weighted — international curricula and English-taught options.",
        "transport": "Transport weighted — rail and urban mobility in the mix.",
        "nature": "Nature weighted — greenery, mountains, coast all count.",
        "culture": "Culture weighted — food, arts, and urban vibrancy.",
        "ru-community": "Russian-speaking diaspora weighted — community density noted.",
        "sports": "Sports weighted — ski, cycling, hiking infrastructure.",
        "_multi": "{{count}} priorities locked in — we'll weight them all."
      },
      "income": {
        "remote": "Remote work — your income country shapes the tax picture.",
        "local": "Local employment — language and job market move up.",
        "business": "Own business — corporate tax and banking are key filters.",
        "passive": "Passive income — tax treaties are your main constraint.",
        "_multi": "Mixed income profile — tax and jobs both weighted."
      },
      "family": {
        "solo": "Flying solo — maximum flexibility.",
        "partner": "Moving with your partner — two lives to optimize.",
        "kids-preschool": "Small children — preschool availability and paediatrics noted.",
        "kids-school": "School-age children — international schools weighted in.",
        "pets": "Pets traveling — quarantine rules will factor in.",
        "_multi": "{{count}} people moving — we'll account for everyone."
      },
      "languages": {
        "en": "English — well served across most of the list.",
        "it": "Italian — opens southern Europe wide.",
        "es": "Spanish — Spain and LATAM options.",
        "de": "German — DACH region a strong fit.",
        "fr": "French — France, Belgium, Switzerland.",
        "pt": "Portuguese — Portugal and Brazil.",
        "any": "Language open — no filter applied.",
        "ru-only": "Only native language — diaspora-friendly zones and expat communities weighted.",
        "_multi": "{{count}} languages noted — good coverage of European options."
      },
      "dealbreakers": {
        "hot-summer": "No hot summers — removing countries over 35°C in July.",
        "snow": "No snowy winters — alpine options reduced.",
        "humidity": "Low humidity — humid coastal climates excluded.",
        "earthquakes": "Low seismic risk — Italy, Greece, Turkey deprioritised.",
        "complex-visa": "Simple visa required — filtering for straightforward paths.",
        "expensive-healthcare": "Affordable healthcare — private-system countries reduced.",
        "_multi": "{{count}} hard filters active — non-matching countries will be excluded."
      },
      "budget": {
        "1": "Under EUR 500 — strong match in Eastern Europe and Southeast Asia.",
        "2": "EUR 500–1000 — 15+ countries remain fully viable.",
        "3": "EUR 1000–2000 — Western and Southern Europe in reach.",
        "4": "EUR 2000+ — no budget constraints applied."
      },
      "ties": {
        "_single": "We'll give that country a meaningful boost in your results.",
        "_multi": "We'll boost all {{count}} countries with your prior ties in the rankings."
      }
    },
    "cost": {
      "disclaimer": "Cost figures are crowd-sourced from livingcost.org, cross-checked against official data where it exists. They're self-reported by residents, not audited — read them as a starting estimate, not a measurement. They don't include tax, one-off relocation costs, or mortgage principal."
    }
  },
  "countries": {
    "meta": {
      "title": "Atlas of 39 countries — Lodestar",
      "description": "Browse 39 relocation destinations scored on climate, cost, tax, visas, healthcare, language, safety and family. Filter by region and visa pathway."
    },
    "title": "All destinations",
    "subtitle": "39 countries — scored on climate, cost, tax, visa, and lifestyle.",
    "filter": {
      "all": "All",
      "europe": "Europe",
      "asia": "Asia",
      "latam": "Americas",
      "middleeast": "Middle East",
      "africa": "Africa & Islands"
    },
    "tier": {
      "T0": "T0 · Pioneer",
      "T1": "T1 · Live data",
      "T2": "T2 · Coming soon",
      "T3": "T3 · Planned",
      "tooltipT0": "Pioneer — full granular comune-level data + live map.",
      "tooltipT1": "Live data — country-level metrics. Map shipping next.",
      "tooltipT2": "Coming soon — data collection in progress.",
      "tooltipT3": "Planned — on the roadmap."
    },
    "openMap": "Open map →",
    "openGuide": "Open guide →",
    "readGuide": "Read the guide →",
    "checklist": "Checklist →",
    "checklistColLabel": "Checklist",
    "comingSoon": "Coming soon",
    "startQuiz": "Find my match →",
    "browseAll": "Browse all countries",
    "backToResult": "Back to results",
    "excluded": "Filtered out",
    "noData": "Coming soon",
    "noResults": "No countries in this region yet.",
    "subtitlePersonal": "Ranked by your quiz results — highest match first.",
    "personalBanner": "Sorted by your quiz match. Countries where you score higher appear first.",
    "guidePromo": {
      "title": "New here? Start with the Field Guide",
      "subtitle": "How to use the map, pins, shortlist and compare table."
    },
    "scoreBand": {
      "excellent": "Excellent",
      "strong": "Strong",
      "fair": "Fair",
      "weak": "Weak"
    },
    "topThree": "Your top three",
    "sort": {
      "label": "Sort",
      "match": "Match",
      "tier": "Tier",
      "name": "Name",
      "cost": "Cost",
      "tax": "Tax"
    },
    "view": {
      "label": "View",
      "grid": "Grid",
      "list": "List"
    },
    "metrics": {
      "costShort": "{{amount}}/mo",
      "taxShort": "{{rate}}% tax",
      "costLabel": "Cost",
      "taxLabel": "Tax",
      "costHelper": "Rent, groceries, utilities, transport, health and everyday spending. Childcare and schooling are counted separately.",
      "taxHelper": "Top personal income tax rate",
      "dataNote": "Numbers below are typical monthly cost for a small family (rent + utilities + groceries, in EUR) and the country's top personal income tax rate.",
      "costIn": "{{household}} · {{city}}",
      "costHousehold": {
        "single": "one person",
        "couple": "a couple",
        "family_1": "two adults, one child",
        "family_2": "two adults, two children",
        "family_3": "two adults, three children"
      }
    },
    "visa": {
      "label": "Visa path",
      "any": "Any path",
      "nomad": "Digital nomad",
      "freelance": "Freelance / self-employed",
      "investment": "Investment / golden"
    },
    "shipping": {
      "title": "Maps shipping next",
      "subtitle": "Live country-level data, full comune-style maps over the next quarters.",
      "quarter": "Q{{q}} {{year}}"
    },
    "excludedSection": {
      "title": "Filtered out ({{count}})",
      "subtitle": "Countries excluded by hard deal-breakers — passport, banking, climate.",
      "expand": "Show",
      "collapse": "Hide"
    },
    "regionFilterLabel": "Region filter",
    "regionName": {
      "africa": "Africa",
      "americas": "Americas",
      "asia": "Asia",
      "caribbean": "Caribbean",
      "caucasus": "Caucasus",
      "europe": "Europe",
      "indianOcean": "Indian Ocean",
      "latam": "Latin America",
      "middleEast": "Middle East",
      "southAmerica": "South America",
      "southeastAsia": "Southeast Asia"
    }
  },
  "header": {
    "signIn": "Sign in",
    "signOut": "Sign out",
    "countryGuides": "Country guides",
    "howTo": "How-to",
    "guides": "Guides",
    "guidesTitle": "Country guides",
    "guidesFull": "Guide",
    "guidesChecklist": "Moving checklist",
    "guidesAll": "All 39 countries →",
    "guidesTopics": "Browse by topic →",
    "guidesTooltip": "Encyclopedic guides per country (Italy, Portugal, Spain, UAE so far)",
    "howToTooltip": "How to use the site — Field Guide",
    "nav": "Primary"
  },
  "language": {
    "switcherAria": "Current language: {{current}}. Change language",
    "listAria": "Language picker"
  },
  "share": {
    "button": "Share",
    "copied": "Copied",
    "title": "Lodestar",
    "tooltip": "Copy link to current view",
    "aria": "Share current map view"
  },
  "a11y": {
    "skipToContent": "Skip to map",
    "mapRegion": "Interactive map of Italy. Screen reader alternative: use the Filters, Layer and POI panels."
  },
  "offline": {
    "banner": "You are offline. Loaded data is still available, new layers may fail to load."
  },
  "loading": {
    "default": "Loading…",
    "data": "Loading data…",
    "list": "Loading list…",
    "profile": "Loading profile…"
  },
  "errors": {
    "root": {
      "title": "Something went wrong",
      "description": "An error occurred. We've been notified and will look into it. Try reloading the page.",
      "reload": "Reload"
    },
    "feature": {
      "fallback": "Failed to load section"
    }
  },
  "filters": {
    "title": "Filters",
    "open": "Open filters",
    "close": "Collapse filters",
    "totalCount": "Total: {{count}} {{unitPlural}}",
    "matched": "Matches:",
    "reset": "Reset filters",
    "noFilterValue": "— off —",
    "altitude": "Altitude",
    "tJulMax": "July T° at most",
    "tJanMin": "January T° at least",
    "humMax": "Humidity at most",
    "snowMin": "Snow days at least",
    "sunnyMin": "Sunny days at least",
    "zonesTitle": "Terrain zone",
    "empty": {
      "title": "Nothing matches",
      "constraintDesc": "Strictest filter: <strong>{{label}}</strong> (removes {{pct}}% of {{unitPlural}}).",
      "relax": "Relax «{{label}}»",
      "fallback": "Try resetting all filters and narrowing your query step by step.",
      "resetAll": "Reset all"
    },
    "matchedAria": "{{count}} matched",
    "matchedZeroAria": "No {{unitPlural}} match current filters"
  },
  "constraints": {
    "alt": "Altitude",
    "tJulMax": "July T°",
    "tJanMin": "January T°",
    "humMax": "Humidity",
    "snowMin": "Snow days",
    "sunnyMin": "Sunny days",
    "zones": "Terrain zone",
    "popMax": "Population",
    "hospMax": "Distance to hospital"
  },
  "zones": {
    "pianura": "Plains",
    "collina_interna": "Hills (inland)",
    "collina_litoranea": "Hills (coastal)",
    "montagna_interna": "Mountains (inland)",
    "montagna_litoranea": "Mountains (coastal)"
  },
  "presets": {
    "my_profile": "Sample profile (demo)",
    "alpine": "Alpine",
    "subalpine": "Subalpine",
    "lakeside": "Lakeside",
    "dolomites": "Dolomites",
    "noheat": "No summer heat"
  },
  "layer": {
    "title": "Map layer",
    "openSheet": "Open layer selector",
    "mode": {
      "label": "Display mode",
      "choropleth": "By zones",
      "heatmap": "Heatmap",
      "none": "Map only",
      "noneHint": "Hide the colour overlay — show just the basemap. Triggers automatically at zoom 15+."
    },
    "addon": {
      "terrain": "Terrain"
    },
    "group": {
      "climate": "Climate",
      "economy": "Economy",
      "tax": "Taxes",
      "infra": "Infrastructure",
      "demo": "Demographics"
    },
    "noData": "no data",
    "filterActive": "Filter active — overlay modified",
    "filterActiveHidden": "Filters are active but the data layer is hidden — you cannot see what matches.",
    "showLayer": "Show data layer",
    "label": {
      "altitude": "Altitude",
      "t_mean": "Annual mean T°",
      "t_jul": "July T°",
      "t_jan": "January T°",
      "humidity": "Humidity",
      "precip": "Precipitation (annual)",
      "snow": "Snow days",
      "sunny": "Sunny days",
      "rent": "Rent €/m²/year",
      "sale": "Sale €/m²",
      "income": "Mean income",
      "internet": "Internet speed",
      "hospital": "Distance to hospital",
      "train": "Distance to train station",
      "density": "Population density",
      "foreign_total": "Foreign residents (%)",
      "foreign_ru": "Russian residents (%)",
      "foreign_ua": "Ukrainian residents (%)",
      "foreign_by": "Belarusian residents (%)",
      "irpef": "Additional IRPEF (%)"
    }
  },
  "poi": {
    "title": "Map points",
    "open": "Open POI",
    "openAria": "Open map points",
    "close": "Collapse",
    "closeAria": "Collapse POI panel",
    "enableAll": "Enable all layers",
    "groupEnableAria": "Enable all in group {{group}}",
    "autoHint": {
      "message": "Turned on basic infrastructure: hospitals, pharmacies, schools, stations.",
      "optInPrompt": "Show essential infrastructure (hospitals, pharmacies, schools, stations) on the map?",
      "personaPrompt": "Show POI tuned for your profile ({{label}})?",
      "enable": "Show",
      "configure": "Customise",
      "dismiss": "Dismiss"
    },
    "group": {
      "transport": "Transport",
      "essentials": "Essentials",
      "sports": "Sports & leisure",
      "kids": "Kids",
      "adults": "Adults"
    },
    "category": {
      "hospitals": "Hospitals",
      "pharmacies": "Pharmacies",
      "schools": "Schools",
      "kindergartens": "Kindergartens",
      "language_schools": "Language schools",
      "stations": "Train stations",
      "airports": "Airports",
      "supermarkets": "Supermarkets",
      "ski_areas": "Ski areas",
      "ski_resorts": "Ski lifts",
      "alpine_huts": "Alpine huts",
      "yoga": "Yoga studios",
      "gym": "Gyms",
      "courts": "Sport courts (tennis/padel/multi)",
      "pools": "Pools",
      "coworking": "Coworking",
      "weather_stations": "Weather stations"
    }
  },
  "places": {
    "title": "Places",
    "open": "Open places",
    "close": "Collapse",
    "closeAria": "Collapse places panel",
    "countSelected": "{{count}} selected",
    "searchPlaceholder": "Search regions, {{unitPlural}}, cities (min. 2 letters)…",
    "removeFromFavorites": "Remove from selection",
    "moreItems": "+{{count}} more",
    "minQueryTitle": "Search {{unit}}",
    "minQueryDesc": "Type at least 2 letters of the name (e.g. «Trento» or «Bolzano»). Add matches to favorites to show them on the map.",
    "noResultsTitle": "Nothing for «{{q}}»",
    "noResultsDesc": "Check keyboard layout or try shorter (2–3 letters).",
    "limitNote": "Showing first {{count}}. Refine your query.",
    "showOnMap": "Show on map",
    "showAllSelected": "Show all on map",
    "clear": "Clear",
    "clearConfirm": "Sure? Delete",
    "clearConfirmTitle": "Click again to delete all"
  },
  "details": {
    "loading": "Loading data…",
    "close": "Close details",
    "closeTooltip": "Close (Esc)",
    "italyGuide": {
      "heading": "Read about Italy",
      "visa": "Visa pathways",
      "taxes": "Tax regimes",
      "cost": "Cost of living",
      "property": "Buying property",
      "hub": "Full Italy guide →"
    },
    "section": {
      "profileFit": "Profile fit",
      "yearRound": "Year-round trends",
      "climate": "Climate",
      "infra": "Infrastructure",
      "internet": "Internet",
      "demo": "Demographics",
      "tax": "Taxes",
      "foreign": "Foreign residents",
      "econ": "Economy",
      "propertyTax": "Property tax (IT)"
    },
    "monthly": {
      "temp": "Temperature by month",
      "precip": "Precipitation by month",
      "humidity": "Humidity by month",
      "sunny": "Sunny days by month",
      "disclaimer": "Seasonal estimate from annual aggregates. Exact monthly values arrive with the next pipeline iteration.",
      "realData": "Real NASA POWER climate normals 1991–2020, downscaled by elevation.",
      "snow": "Snow days by month"
    },
    "kpi": {
      "altitude": "Altitude",
      "tMean": "T° avg",
      "hospital": "Hospital",
      "internet": "Internet"
    },
    "photo": {
      "source": "Wikipedia · italiano",
      "openOnWikipedia": "Open on Wikipedia"
    },
    "row": {
      "tMean": "Annual mean T°",
      "tJul": "July T°",
      "tJan": "January T°",
      "hotDays": "Hot days",
      "humidity": "Humidity",
      "precip": "Precipitation",
      "snowDays": "Snow days",
      "sunnyDays": "Sunny days",
      "sunHours": "Total sun hours",
      "toHospital": "To hospital",
      "toPharmacy": "To pharmacy",
      "toSchool": "To school",
      "toTrain": "To train station",
      "toAirport": "To airport",
      "toSupermarket": "To supermarket",
      "toHut": "To alpine hut",
      "toSki": "To ski lift",
      "skiN20": "Ski lifts in 20km",
      "dl": "Download speed",
      "ul": "Upload speed",
      "ping": "Ping",
      "population": "Population",
      "density": "Density",
      "zone": "Terrain zone",
      "irpefMax": "Additional IRPEF (max rate)",
      "irpefExempt": "Income exemption threshold",
      "foreignTotal": "Total foreign",
      "foreignRu": "🇷🇺 Russia",
      "foreignUa": "🇺🇦 Ukraine",
      "foreignBy": "🇧🇾 Belarus",
      "rent": "Rent",
      "sale": "Sale",
      "income": "Mean income"
    },
    "collapse": "Collapse panel",
    "expand": "Expand panel",
    "propertyTax": {
      "perComuneNote": "Per-comune IMU multiplier not yet in dataset — figures here are national bands."
    }
  },
  "search": {
    "inputAria": "Search {{unit}} or city",
    "clearAria": "Clear search",
    "favoritesOpen": "Open favorites",
    "favoritesAria": "Favorites: {{count}}",
    "starAdd": "Add to favorites",
    "starRemove": "Remove from favorites",
    "flyTo": "Fly to {{name}}",
    "removeNamed": "Remove {{name}}",
    "geocodeFallback": "Search by city/town name (OSM)",
    "geocodeLoading": "Searching OpenStreetMap…",
    "geocodeNoMatch": "No match in {{country}}. Try a different spelling or the {{unit}} name.",
    "geocodeFound": "Open",
    "cyrillicHint": "Italian place names are stored in Latin script. Try \"Trento\" instead of \"Тренто\".",
    "section": {
      "regions": "Regions",
      "comuni": "{{unitPlural}}",
      "places": "Other places (OSM)"
    },
    "comuniSuffix": "{{unitPlural}}"
  },
  "login": {
    "title": "Sign in to Lodestar",
    "google": "Sign in with Google",
    "or": "or",
    "emailPlaceholder": "email@example.com",
    "submit": "Get sign-in link",
    "submitting": "Sending…",
    "sent": "Link sent",
    "sentHint": "Check your inbox (including spam). The link is valid for 15 minutes.",
    "back": "← Back to map",
    "errorSend": "Failed to send link",
    "errorGeneric": "Send error",
    "errorGoogle": "Google OAuth error"
  },
  "shortlist": {
    "title": "My lists",
    "open": "Open lists",
    "empty": "You don't have any lists yet",
    "emptyHint": "Save {{unitPlural}} to compare them later",
    "create": "Create list",
    "createPlaceholder": "List name",
    "createSubmit": "Create",
    "rename": "Rename",
    "remove": "Delete list",
    "removeConfirm": "Delete this list?",
    "guestHint": "Lists are saved in this browser. Sign in to access them from any device.",
    "syncingDirty": "Saving…",
    "syncedLabel": "Saved",
    "save": "Save",
    "saved": "Saved",
    "unsave": "Remove from saved",
    "addToList": "Add to list",
    "removeFromList": "Remove from list",
    "items": "{{count}} {{unitPlural}}",
    "items_one": "{{count}} {{unit}}",
    "items_other": "{{count}} {{unitPlural}}",
    "notePlaceholder": "Add a note (visible only to you)",
    "noteSave": "Save",
    "viewOnMap": "Show all on map",
    "compare": "Compare",
    "exportCsv": "Export CSV",
    "activeBadge": "Active",
    "selectActive": "Make active",
    "deletedConfirm": "List deleted",
    "loginToSync": "Sign in to sync",
    "close": "Close",
    "planVisit": "Plan visit",
    "planVisitTrim": "Routes only first 7 of {{count}} stops.",
    "checklist": "Moving checklist",
    "checklistHint": "Next steps after deciding: visa, codice fiscale, residency"
  },
  "pins": {
    "nearbyTitle_one": "{{count}} of your pins nearby",
    "nearbyTitle_other": "{{count}} of your pins nearby",
    "nearbyMore": "+{{count}} more",
    "nearbyDistance": "{{km}} km",
    "nearbyInComune": "in this {{unit}}",
    "nearbyOpen": "Show on map",
    "demoBadge": "demo",
    "demoHint": "Sample listing. Sign in to save your own pins.",
    "demoWhat": "What is this?",
    "demoWhatBody": "A demo pin — a sample listing. This is how your saved places will look.",
    "demoWhy": "Why is it here?",
    "demoWhyBody": "The map shows demo data to anonymous visitors so it isn't empty.",
    "demoHow": "How to use?",
    "demoHowBody": "Sign in and save listings you like — they will appear here with climate and infrastructure scores.",
    "demoHide": "✕ Hide demo pins",
    "demoSignIn": "Sign in →",
    "deleteConfirm": "Delete this pin?",
    "delete": "Delete",
    "add": "Pin this place",
    "addTitle": "Pin this place",
    "bulk": {
      "addresses": "Addresses",
      "cappedAt": "(limited to first {{max}} per batch)",
      "csv": "CSV",
      "importing": "Importing…",
      "openLabel": "Import",
      "openTitle": "Import a shortlist of places",
      "parsed": "Parsed {{count}} addresses",
      "parsedUrls": "Parsed {{count}} URLs",
      "progress": "{{done}} of {{total}}",
      "start": "Import",
      "statusExtracting": "extracting",
      "statusGeocoding": "geocoding",
      "statusSaving": "saving",
      "subtitle": "Paste addresses (one per line) or a CSV. We geocode each with Nominatim — about 1 second per address.",
      "subtitleUrls": "Paste listing URLs (one per line). We extract coordinates from the page — works with Immobiliare.it, Idealista.it, Casa.it and others.",
      "summary": "Imported {{ok}}; {{fail}} failed.",
      "title": "Import a shortlist of places",
      "urls": "URLs"
    },
    "errorGeneric": "Could not save pin",
    "fieldNotes": "Notes",
    "fieldTitle": "Title",
    "fieldUrl": "Listing URL (optional)",
    "nearbyTitle": "Pins nearby",
    "openComune": "Open {{unit}} metrics",
    "openComuneNamed": "Metrics for {{name}}",
    "save": "Save pin",
    "saving": "Saving…",
    "shortAdd": "Pin",
    "titleRequired": "Title is required",
    "validation": {
      "compare": "Compare →",
      "indicator": "You have {{count}} places on the map"
    }
  },
  "account": {
    "title": "Account",
    "email": "Email",
    "name": "Name",
    "namePlaceholder": "Your name",
    "save": "Save",
    "saved": "Saved",
    "saving": "Saving…",
    "preferences": "Preferences",
    "language": "Language",
    "theme": "Theme",
    "themeLight": "Light",
    "themeDark": "Dark",
    "themeAuto": "System",
    "defaultLayer": "Default layer on the map",
    "stats": "Your data",
    "statsLists_one": "{{count}} list",
    "statsLists_other": "{{count}} lists",
    "statsProfiles_one": "{{count}} profile",
    "statsProfiles_other": "{{count}} profiles",
    "statsPins_one": "{{count}} pin",
    "statsPins_other": "{{count}} pins",
    "danger": "Danger zone",
    "deleteAccount": "Delete account",
    "deleteHint": "Your account and all data (lists, profiles, pins) will be permanently deleted.",
    "deleteConfirmTitle": "Delete account?",
    "deleteConfirmBody": "This is irreversible. Type \"DELETE\" to confirm.",
    "deleteConfirmInput": "DELETE",
    "deleteConfirmCta": "Delete forever",
    "cancel": "Cancel",
    "back": "← Back to map",
    "signOut": "Sign out",
    "notSignedIn": "You're not signed in",
    "signIn": "Sign in",
    "matchTitle": "Your match",
    "matchEmpty": "You haven't taken the quiz yet.",
    "matchEmptyCta": "Take the 2-minute quiz",
    "matchLoading": "Loading your saved match…",
    "matchOpenFull": "Open full result",
    "matchRetake": "Retake quiz",
    "matchTakenOn": "Taken on {{date}}",
    "matchRank": "#{{rank}}",
    "checklistsTitle": "Saved checklists",
    "checklistsEmpty": "You haven't pinned any countries yet. Pin a place from the map or save a checklist from a country page.",
    "checklistsBrowse": "Browse 39 countries",
    "checklistOpen": "Open checklist",
    "checklistsInfoTitle": "Useful info for your saved places",
    "checklistsInfoTax": "Tax",
    "checklistsInfoCar": "Car",
    "checklistsInfoVisa": "Visa",
    "checklistsInfoNoData": "No data yet",
    "exportCta": "Export my data",
    "exportHint": "Download a JSON copy of your profiles, shortlists, pins and preferences.",
    "exporting": "Preparing…",
    "exportTitle": "Your data",
    "statsLists": "Lists",
    "statsPins": "Pins",
    "statsProfiles": "Profiles"
  },
  "gdpr": {
    "bannerTitle": "Cookies & privacy",
    "bannerBody": "We use only essential cookies (sign-in session) and anonymous analytics. No third-party trackers, no ads.",
    "accept": "Got it",
    "details": "Read more",
    "privacy": "Privacy policy",
    "terms": "Terms of service"
  },
  "propertyTax": {
    "summaryTitle": "Owning a home here",
    "label": "Property tax",
    "loading": "Loading…",
    "noData": "No property-tax data for this country yet.",
    "placeholder": "Specific rates for this country are still being verified — ask the AI helper or a consultant for current numbers.",
    "tier": {
      "low": "Low",
      "medium": "Medium",
      "high": "High"
    },
    "row": {
      "annual": "Annual tax",
      "purchase": "Purchase tax (one-shot)",
      "capitalGains": "Capital gains on sale",
      "wealth": "Wealth tax on real estate",
      "regime": "Foreigner regime"
    },
    "value": {
      "noAnnualTax": "No annual tax",
      "primary": "primary",
      "primaryExempt": "primary exempt",
      "secondHome": "second home",
      "primaryAlwaysExempt": "primary always exempt",
      "primaryExemptAfter": "primary exempt after {{years}}y",
      "secondHomeExemptAfter": "second home exempt after {{years}}y",
      "over65PrimaryExempt": "over-65 primary exempt",
      "lifetimeExemption": "€{{amount}} lifetime exemption",
      "threshold": "€{{amount}}k threshold"
    }
  },
  "carOwnership": {
    "summaryTitle": "Car ownership",
    "chipMonthly": "~€{{amount}}/mo",
    "comparePage": {
      "label": "Car ownership (country-level)",
      "note": "Same nationally for all listed comuni — country-level estimate."
    },
    "quizCard": {
      "tier": {
        "very_low": "Low car cost",
        "low": "Low car cost",
        "medium": "Mid car cost",
        "high": "High car cost",
        "very_high": "Very high car cost"
      }
    }
  },
  "compare": {
    "title": "Comparison",
    "needMore": "Select at least 2 comuni to compare. Open \"My lists\" and click \"Compare\".",
    "notFound": "Comuni not found for the given ISTAT codes.",
    "metric": "Metric",
    "hint": "Green = best in row, red = worst (for distances and hot days, lower is better).",
    "copyLink": "↗ Share link",
    "linkCopied": "✓ Copied!",
    "exportCsv": "⬇ CSV",
    "print": "⎙ Print / PDF",
    "queued_one": "{{count}} selected",
    "queued_other": "{{count}} selected",
    "clearAll": "Clear compare",
    "barAriaLabel": "Compare queue",
    "go": "Compare →",
    "units": {
      "m": "m",
      "km": "km",
      "celsius": "°C",
      "percent": "%",
      "mm": "mm",
      "mbps": "Mbps",
      "ms": "ms",
      "personsPerKm2": "people/km²"
    },
    "add": "Compare",
    "emptyEyebrow": "Side-by-side comparison",
    "emptyLead": "This page puts up to 5 Italian comuni side-by-side across climate, infrastructure, internet, taxes, and demographics — so you can spot the trade-offs at a glance instead of clicking between tabs.",
    "emptyMapCta": "Open Italy map",
    "emptyMetric": "Metric",
    "emptyQuizCta": "Or take the country quiz",
    "emptyRecoCta": "See top regions for me",
    "emptySampleLabel": "What it looks like once filled",
    "emptySampleNote": "Highlighted cells = best in row",
    "emptyStepsAria": "How to fill the comparison",
    "emptyTitle": "No comuni in your comparison yet.",
    "full": "Compare list is full (max 5)",
    "inList": "Comparing",
    "propertyTax": {
      "label": "Property tax (country-level)",
      "note": "Same nationally for all listed comuni — per-comune IMU multipliers not yet in dataset."
    },
    "queued": "{{count}} selected",
    "remove": "Remove from compare",
    "step1": {
      "body": "Browse Italy by zone or jump straight to regions the quiz scored high.",
      "title": "Open the map or recommender"
    },
    "step2": {
      "body": "In the details panel for any comune, hit the “⊞ Compare” chip. Repeat 2–5 times.",
      "title": "Click a comune → ⊞ Compare"
    },
    "step3": {
      "body": "Hit the “Compare” button in your shortlist drawer to land back on this page with data filled in.",
      "title": "Return here"
    }
  },
  "privacy": {
    "title": "Privacy policy",
    "back": "← Back",
    "updated": "Last updated: {{date}}",
    "intro": "Lodestar is an open project that helps people compare cities and countries for relocation. We collect the minimum amount of information needed to make the app work and improve it.",
    "store": {
      "title": "What we store",
      "account": "<b>Account</b> — email address (only if you sign in), display name (optional), authentication sessions.",
      "personal": "<b>Personal data you create</b> — lists of saved comuni, notes, scoring profiles, pins. Stored in our Cloudflare D1 database, tied to your account.",
      "analytics": "<b>Anonymous analytics</b> — page views, button clicks, performance metrics (LCP/INP/CLS). No advertising trackers and no third-party analytics. We may store an anonymous session identifier and your country (from the request IP) for aggregate stats.",
      "logs": "<b>Logs</b> — error reports via Sentry (when enabled). Stack traces and request metadata, no PII."
    },
    "noDo": {
      "title": "What we don't do",
      "noSell": "We don't sell or share your data with advertisers.",
      "noTrackers": "We don't use third-party cookies, fingerprinting, or behavioural ad networks.",
      "noPayment": "We don't store payment information (the app is free)."
    },
    "partners": {
      "title": "Sharing with partner consultants",
      "body": "When you submit a consultation request and tick the consent checkbox, we may share your contact details (name, email, optional phone) and the brief you provided (target country, service category, timeline) with one or more vetted partner consultants. Partners act as independent data controllers after they accept your request and are bound by a Data Processing Agreement with us. You can withdraw consent at any time by emailing ",
      "after": ". Lawful basis: your explicit consent (GDPR Art. 6(1)(a)) and performance of the introduction service you requested (Art. 6(1)(b))."
    },
    "rights": {
      "title": "Your rights (GDPR)",
      "access": "<b>Access</b> — download all your data via the API or by emailing us.",
      "erasureBefore": "<b>Erasure</b> — use the «Delete account» button on the ",
      "erasureLink": "account page",
      "erasureAfter": ". This is permanent and irreversible.",
      "portability": "<b>Portability</b> — export shortlists as CSV from the shortlist drawer."
    },
    "contact": {
      "title": "Contact",
      "before": "For questions or requests: ",
      "after": "."
    }
  },
  "terms": {
    "title": "Terms of service",
    "back": "← Back",
    "updated": "Last updated: {{date}}",
    "s1": {
      "title": "1. What this service is",
      "body": "Lodestar is a free, open-source tool that aggregates publicly available data (ISTAT, Open-Meteo, CHELSA, OpenStreetMap, MEF) to help users compare cities. It is informational and does not constitute legal, tax, financial, or immigration advice."
    },
    "s2": {
      "title": "2. No warranty",
      "body": "Data is provided «as is» with no guarantee of accuracy or completeness. We try to keep it fresh but you should verify critical decisions against authoritative sources."
    },
    "s3": {
      "title": "3. Acceptable use",
      "personal": "Use the service for personal, non-commercial relocation research.",
      "noScrape": "Don't scrape the API at high rate (rate limits apply).",
      "noAbuse": "Don't abuse public-profile sharing for spam or harassment."
    },
    "s4": {
      "title": "4. Your content",
      "body": "Profiles you mark as «public» are visible to others. Everything else (lists, pins, notes) is private to your account. You retain ownership of content you create."
    },
    "s5": {
      "title": "5. Changes",
      "body": "We may update these terms. Major changes will be highlighted in the app. Continued use after a change constitutes acceptance."
    },
    "s6": {
      "title": "6. Contact",
      "before": "See our ",
      "link": "privacy policy",
      "after": " for data-related questions."
    },
    "s7": {
      "title": "7. Purchases and payments",
      "body": "Some parts of the service are paid. The Personal Relocation Report is a one-time digital purchase (€29) generated automatically from the answers you give in the quiz and delivered instantly as a private link you keep. It is informational guidance, not legal, tax, or immigration advice. Payments are processed by Lemon Squeezy, our Merchant of Record, who also handles invoicing and applicable taxes. Refunds are governed by our ",
      "link": "Refund policy",
      "after": "."
    }
  },
  "property": {
    "sectionTitle": "For sale / for rent",
    "sale": "For sale",
    "rent": "For rent",
    "count": "listings",
    "sourceNote": "Listings aggregated from open data — verify on the source before contacting."
  },
  "reco": {
    "backHome": "Back to Lodestar",
    "landing": {
      "title": "Pick the right region of Italy — not just the right country.",
      "subtitle": "Italy has 20 regions, 7,896 comuni, and three climates. \"Italy 87%\" tells you nothing about where to actually live. This page narrows that down to a top-5 ranking — alpine vs Mediterranean, urban vs rural, expensive vs cheap — using your relocation profile.",
      "howLabel": "How it works",
      "step1Title": "Take the 2-minute quiz",
      "step1Body": "10 questions about climate, budget, family, and priorities. The same quiz that ranks the 31 countries — we use those answers for regions too. No separate region quiz to take.",
      "step2Title": "We score the 20 Italian regions",
      "step2Body": "Each region gets a match% on four axes — climate, cost, infrastructure, internet — with 2-3 plain-language reasons. Sample below.",
      "step3Title": "Drill into the top 10 comuni",
      "step3Body": "Expand any region to see the 10 highest-matching comuni inside it — with altitude, score, and a short note on why each fits. Click through to the comune-level map.",
      "sampleLabel": "A sample result",
      "sampleRegion": "Trentino-Alto Adige",
      "sampleMeta": "282 comuni · 1.07M residents",
      "sampleReason1": "Alpine climate matches your priority (avg 600m+, snow days 25+/yr).",
      "sampleReason2": "Median rent €11.4/m² — 30% below Milan, 15% below Florence.",
      "sampleReason3": "Highest hospital + ski-lift density in northern Italy.",
      "sampleNote": "Sample data. Your actual results depend on your quiz answers.",
      "personasLabel": "Different profiles, different rankings",
      "personasBody": "We detect five archetypes from your quiz — each weights climate, cost, and infrastructure differently. A family with kids and a digital nomad shouldn't be told to live in the same place.",
      "ctaLabel": "Start",
      "cta": "Take the 2-minute quiz",
      "ctaSkip": "Or browse the full Italy map (8,000 comuni, no filter)",
      "ctaHint": "No signup. Anonymous. Quiz takes ~2 minutes."
    },
    "needQuiz": "Take the quiz first — we need your profile to rank Italian regions.",
    "startQuiz": "Take the quiz →",
    "backToResult": "Back to quiz result",
    "title": "Top Italian regions for your profile",
    "subtitle": "Ranked from your quiz answers — climate, cost, infrastructure, internet. Click a region to open the map zoomed in.",
    "noData": "Region dataset not available yet.",
    "matchLabel": "Match",
    "openMap": "Open map here →",
    "italyContext": "Showing regions of Italy because Italy topped your quiz result.",
    "showComuni": "Top 10 comuni →",
    "hideComuni": "Hide top comuni",
    "personaBadge": "Detected profile: {{label}}",
    "minimap": {
      "title": "Where this region sits in Italy",
      "caption_one": "{{count}} comune · region highlighted",
      "caption_other": "{{count}} comuni · region highlighted",
      "caption": "{{count}} comuni · region highlighted"
    },
    "dim": {
      "climate": "Climate",
      "cost": "Cost",
      "infra": "Infra",
      "internet": "Internet"
    },
    "reason": {
      "alpineClimate": "Alpine character: median altitude {{alt}} m",
      "mediterraneanWarmth": "Mediterranean warmth: median July {{temp}} °C",
      "continentalSeasons": "Continental seasons: January {{tJan}} °C, July {{tJul}} °C",
      "climateFit": "Climate fits your selection",
      "climateAvg": "Climate fit: avg {{temp}} °C, {{sunny}} sunny days/year",
      "climateAvgTemp": "Climate fit: avg {{temp}} °C, {{tJul}} °C in July",
      "affordable": "Affordable for your budget: ~{{rent}} €/m²/year",
      "strongInternet": "Strong connectivity: {{mbps}} Mbps median",
      "infrastructureAccess": "Infrastructure within reach: hospital ~{{hospital}} km, train ~{{train}} km",
      "transitAccess": "Transit within reach: train ~{{train}} km, airport ~{{airport}} km",
      "familyReady": "Family-ready: school ~{{school}} km, hospital ~{{hospital}} km",
      "outdoorAccess": "Outdoor access: ski lift ~{{ski}} km"
    }
  },
  "persona": {
    "family": "Family with kids",
    "nomad": "Remote / digital nomad",
    "sport": "Outdoor / mountain sports",
    "quiet": "Quiet village life",
    "urban": "City / culture-focused"
  },
  "ai": {
    "openBubble": "Ask the relocation helper",
    "close": "Close",
    "title": "Lodestar helper",
    "placeholder": "Ask a question…",
    "submit": "Ask",
    "thinking": "Thinking…",
    "emptyAnswer": "Empty answer — try rephrasing.",
    "networkError": "Network error.",
    "stubBadge": "Stub response — Anthropic API key not set on this deployment.",
    "intro": {
      "details": "Ask anything about this {{unit}} — climate, infrastructure, taxes, demographics. Answers come from the data already loaded; no fabricated numbers.",
      "quiz": "Confused by a quiz question? Ask what it means or how it'll affect your ranking.",
      "quiz_result": "Ask why a country ranks where it does, or compare two of them.",
      "recommendations": "Ask why a region is ranked here, or compare two regions.",
      "map": "Ask about the active layer, current filters, or visible {{unitPlural}}.",
      "next_steps": "Ask about a checklist item: what it means, where to start, who to contact."
    },
    "askAnother": "← Ask another",
    "contextLabel": "Context",
    "dropContext": "Drop this scope",
    "suggestedLabel": "Try asking"
  },
  "tour": {
    "next": "Next",
    "skip": "Skip",
    "done": "Got it",
    "step": "Step {{current}} / {{total}}",
    "search": {
      "title": "Search {{unitPlural}}",
      "body": "Search any {{unit}} by name. Click a result to jump there."
    },
    "layer": {
      "title": "Map layers",
      "body": "Switch the map view: temperature, prices, internet speed, immigrant communities, and more."
    },
    "poi": {
      "title": "Points of interest",
      "body": "Toggle hospitals, schools, transit, ski lifts and other points of interest."
    },
    "shortlist": {
      "title": "Your shortlists",
      "body": "Save {{unitPlural}} you like into named lists with notes — sign in to access them anywhere."
    }
  },
  "scoring": {
    "profileFit": "Profile fit",
    "changeProfile": "Change profile",
    "weightTooltip": "Weight in this profile",
    "dimensions": {
      "climate": "Climate",
      "cost": "Cost of living",
      "infra": "Infrastructure",
      "foreign": "Expat community",
      "tax": "Local tax"
    },
    "presets": {
      "balanced": {
        "name": "Balanced",
        "description": "Equal weight on all factors"
      },
      "family": {
        "name": "Family with kids",
        "description": "Schools, hospitals, and affordability first"
      },
      "nomad": {
        "name": "Digital nomad",
        "description": "Good internet, mild climate, affordable"
      },
      "retiree": {
        "name": "Retiree",
        "description": "Healthcare, mild climate, low taxes"
      },
      "mountain": {
        "name": "Mountain enthusiast",
        "description": "Altitude, cool summers, snow"
      }
    }
  },
  "locale": {
    "show": "Show",
    "hint": {
      "ru": "Russian-speaking residents layer available — see where your community is.",
      "ua": "Ukrainian residents layer available — see community distribution.",
      "de": "German-speaker layer available — useful for Alto Adige / South Tyrol."
    }
  },
  "map": {
    "meta": {
      "title": "{{country}} relocation map — Lodestar",
      "description": "Explore {{country}} region by region: cost of living, rent, climate and services mapped down to the municipality. Filter by your priorities and save what fits."
    },
    "quizBanner": {
      "label": "Quiz filters active",
      "reset": "Reset",
      "dismiss": "Dismiss"
    },
    "focusBanner": {
      "label_one": "{{count}} recommended comune · {{region}}",
      "label_other": "{{count}} recommended comuni · {{region}}",
      "regionFallback": "your region",
      "clear": "Clear",
      "label": "Focused on your quiz profile"
    },
    "backTo": {
      "recommendations": "My recommendations",
      "quizResult": "Quiz result",
      "countries": "Countries",
      "account": "My account"
    },
    "region": {
      "ariaLabel": "Interactive map of {{country}}. Screen-reader alternative: use the Filters, Layer, and POI panels."
    }
  },
  "nextSteps": {
    "title": "Moving to Italy — next steps",
    "titleCountry": "Moving to {{country}} — next steps",
    "subtitle": "A practical checklist from decision to settlement. Your progress is saved locally.",
    "guide": {
      "eyebrow": "Read the full guide",
      "hubLink": "{{country}} encyclopedia →",
      "visa": "Visa",
      "taxes": "Taxes",
      "cost": "Cost of living",
      "banking": "Banking",
      "property": "Property",
      "healthcare": "Healthcare"
    },
    "backToMap": "Back to map",
    "backToResult": "Back to quiz results",
    "consultantPrompt": "Got a question this checklist can't answer? We can introduce you to a vetted local expert.",
    "consultantLink": "Request an introduction →",
    "resources": "Resources",
    "disclaimer": "This checklist is a general guide. Requirements vary by nationality, visa type, and comune. Consult an immigration lawyer for your specific situation.",
    "save": {
      "cta": "Save this checklist",
      "placeholder": "you@example.com",
      "submit": "Save & email me a link",
      "sending": "Saving…",
      "cancel": "Cancel",
      "anonConfirm": "Check your inbox — we sent a one-time sign-in link so you can return to this checklist from any device.",
      "failed": "Not saved — the link could not be sent. Try again.",
      "authConfirm": "Saved — progress is tied to your account."
    },
    "print": {
      "cta": "Print / Save as PDF",
      "hint": "Opens your browser print dialog — choose \"Save as PDF\" to keep a copy offline."
    },
    "pdf": {
      "download": "Download PDF",
      "downloading": "Generating…",
      "downloadHint": "Download a branded PDF of this checklist",
      "downloadError": "Could not generate the PDF. Please try again.",
      "emailCta": "Email checklist",
      "emailHint": "Send the PDF to your inbox",
      "emailPlaceholder": "you@example.com",
      "send": "Send to email",
      "sending": "Sending…",
      "emailSuccess": "Sent to {{email}} — check your inbox.",
      "emailError": "Could not send the email. Please try again.",
      "emailRateLimited": "Too many sends from your network. Try again in 15 minutes.",
      "generatedFor": "Generated for {{recipient}} on {{date}}",
      "anonymous": "anonymous",
      "pageOf": "Page {{page}} of {{total}}"
    },
    "resource": {
      "community": "community",
      "official": "official",
      "property": "property search",
      "banking": "banking",
      "health": "health"
    },
    "phase": {
      "strategy": {
        "title": "Strategic planning",
        "timeline": "6–12 months before"
      },
      "prep": {
        "title": "Preparation",
        "timeline": "3–6 months before"
      },
      "logistics": {
        "title": "Logistics",
        "timeline": "1–3 months before"
      },
      "arrival": {
        "title": "Arrival",
        "timeline": "First month"
      },
      "settle": {
        "title": "Settlement",
        "timeline": "Months 2–6"
      }
    },
    "item": {
      "strategy": {
        "visa-research": "Research visa options for your nationality",
        "lawyer": "Consult an Italian immigration lawyer",
        "language": "Start Italian language basics (A2 recommended)",
        "tax-home": "Understand tax implications in your home country",
        "health-zone": "Verify healthcare coverage in your target comune (ASL zone)"
      },
      "prep": {
        "visa-apply": "Apply for Italian visa at your consulate",
        "codice-fiscale": "Get Codice Fiscale from Italian consulate",
        "apartment-search": "Begin apartment search for long-term rental",
        "expat-connect": "Connect with expat communities in your target region",
        "health-insurance": "Arrange international health insurance for transition"
      },
      "logistics": {
        "arrival-accommodation": "Book arrival accommodation (2–4 weeks short-term)",
        "wise-revolut": "Set up Wise or Revolut for EUR transactions",
        "docs-translation": "Prepare certified translations of key documents",
        "notify-home-bank": "Notify home country bank and tax authority",
        "shipping": "Arrange shipping or storage for belongings"
      },
      "arrival": {
        "residenza": "Register at the comune (ufficio anagrafe) → get residenza",
        "permesso": "Non-EU: apply for permesso di soggiorno at questura (within 8 days of arrival)",
        "medico-base": "Sign up with a local doctor (medico di base) at ASL",
        "bank-account": "Open Italian bank account (Fineco, Buddybank, or online)",
        "ssn": "Register for the Italian National Health Service (SSN)"
      },
      "settle": {
        "commercialista": "Find an accountant (commercialista) for Italian tax filing",
        "driver-license": "Convert driver's license if required by your nationality",
        "utilities": "Set up utilities: gas, electricity, internet",
        "community": "Join local expat groups and integrate with the community",
        "school": "Register children in local school (if applicable)",
        "property-tax-obligations": "Understand property-tax obligations if you plan to buy (IMU + purchase tax + capital gains)"
      }
    },
    "detail": {
      "prep": {
        "codice-fiscale": "Required for almost everything: bank, rental contract, healthcare"
      },
      "arrival": {
        "permesso": "Late application incurs fines; bring all documents on day one"
      },
      "settle": {
        "commercialista": "Even simple tax situations often need local expertise in Italy",
        "property-tax-obligations": "IMU on primary residence is exempt; on second homes 0.5–1.06% of cadastral value. Registration tax 2% primary or 9% second home. Capital gains 26% if sold within 5 years."
      }
    },
    "noData": "We don't have a relocation checklist for {{country}} yet. The 9 countries with checklists are AE, PT, ES, CY, TH, MY, ID, GR, MU (plus IT)."
  },
  "visitPlanner": {
    "title": "Visit plan",
    "subtitle": "Optimal driving order for {{count}} stops.",
    "tooFew": "Select at least 2 comuni to plan a trip.",
    "tooMany": "Up to 7 stops per trip. You picked {{count}}.",
    "computing": "Computing route…",
    "error": "Could not compute route: {{msg}}",
    "drive": "Drive",
    "distance": "Distance",
    "stops": "Stops",
    "openGmaps": "Open in Google Maps",
    "downloadGpx": "Download GPX",
    "markVisited": "Mark as visited",
    "markUnvisited": "Mark as unvisited",
    "youAreHere": "Here",
    "nextStop": "Next stop",
    "imHere": "📍 I'm here",
    "locating": "Locating…",
    "premiumBadge": "free",
    "unlockTitle": "Unlock \"I'm here\" mode",
    "unlockDesc": "Geolocation + one-tap navigation to your next stop. Free — no credit card.",
    "unlockBenefitsTitle": "What you unlock",
    "unlockBenefit1": "Live geolocation pin on the map",
    "unlockBenefit2": "Auto-detected next nearest stop in your route",
    "unlockBenefit3": "One-tap deep link into Apple Maps / Google Maps for navigation",
    "unlockBenefit4": "\"Visited\" check-marks that sync across devices when signed in",
    "unlockWhyEmail": "Why we ask: a one-time link lets you re-open this mode on another device and keeps your route history. No newsletter, no card, no spam — opt-out in every email.",
    "unlockPlaceholder": "you@example.com",
    "unlockBtn": "Unlock free",
    "unlockSending": "Unlocking…",
    "unlockFailed": "Not unlocked — the link could not be sent. Try again.",
    "unlockCancel": "Cancel"
  },
  "story": {
    "back": "Back",
    "backHome": "Back to Lodestar →",
    "title": "Share your relocation story",
    "intro": "Two sentences are plenty. What worked, what surprised you, what would you tell yourself a year ago? Accepted stories — anonymised by default — become long-tail content that helps the next person.",
    "email": "Email",
    "country": "Country (optional)",
    "countryPlaceholder": "— select a country —",
    "body": "Your story",
    "placeholder": "e.g. \"Moved to Trento in spring 2026 on a DNV. Hardest part was the codice fiscale appointment — booked 2 months out. Best surprise: rent was 30% lower than I budgeted.\"",
    "publishOk": "OK to publish anonymously (you'll review the final version before it goes live).",
    "submit": "Share my story",
    "sending": "Sending…",
    "thanksTitle": "Thanks — we'll read it personally.",
    "thanksBody": "We just emailed you a confirmation. If we'd like to publish (anonymised by default), we'll reach out within ten working days.",
    "networkError": "Network error."
  },
  "consultant": {
    "back": "Back",
    "backHome": "Back to Lodestar →",
    "title": "Talk to a relocation expert",
    "intro": "When the data on the map and the checklist isn't enough — when you need a person who's done this for someone like you before. Tell us what you need, we'll personally screen the introduction. No commission charged to you.",
    "email": "Email",
    "country": "Country",
    "countryPlaceholder": "Pick a country…",
    "category": "Help with",
    "timeline": "When?",
    "planningToBuyLabel": "Planning to buy property?",
    "planningToBuy": {
      "yes": "Yes",
      "no": "Renting only",
      "unsure": "Not sure yet"
    },
    "planningToBuyYesNote": "Good — we'll look for someone with property-tax and notary experience for this country.",
    "notes": "A sentence on what you need (optional)",
    "notesPlaceholder": "e.g. \"DNV applicant, need a Trento commercialista who handles non-EU clients.\"",
    "submit": "Request an introduction",
    "sending": "Sending…",
    "telegram": "Telegram (optional — for quick replies)",
    "telegramPlaceholder": "@username or phone number",
    "context": "Context:",
    "thanksTitle": "Request received — we'll be in touch.",
    "thanksBody": "We're building the specialist network now. As soon as we find someone who matches your country and topic, we'll reach out. If we can't make a match we'll tell you directly.",
    "networkError": "Network error.",
    "cat": {
      "visa": "Visa & immigration",
      "tax": "Tax & accounting",
      "real-estate": "Real estate",
      "translation": "Translation & documents",
      "other": "Something else"
    },
    "tl": {
      "this_month": "This month",
      "this_quarter": "This quarter",
      "this_year": "This year",
      "exploring": "Just exploring"
    }
  },
  "feedback": {
    "openLabel": "Feedback",
    "openTitle": "Send feedback",
    "errorRateLimit": "Too many submissions. Try again later.",
    "errorGeneric": "Could not submit. Try again.",
    "category": "Category",
    "email": "Email (optional, for reply)",
    "message": "Message",
    "placeholder": "What happened, where, and what did you expect?",
    "send": "Send",
    "sending": "Sending…",
    "subtitle": "Found a bug, missing data, or have an idea? Tell us.",
    "thanks": "Thanks! We read every report.",
    "title": "Send feedback",
    "tooShort": "Add at least a sentence."
  },
  "topics": {
    "landing": {
      "headlineFmt": "{{topic}} across 39 countries",
      "descriptionFmt": "Compare {{topic}} routes country by country. {{published}} chapters published so far; the rest are in the pipeline.",
      "home": "Lodestar",
      "crumb": "Topics",
      "published": "Published chapters",
      "publishedEmpty": "No country chapters yet. Italy is the editorial flagship — chapters land here as they ship.",
      "coming": "Coming next",
      "readChapter": "Read the chapter",
      "others": "Other dimensions",
      "ctaQuiz": "Take the quiz →",
      "ctaAtlas": "Browse the atlas →",
      "notFoundTitle": "Topic not found",
      "notFoundSub": "Pick a dimension from the home page to see all chapters and countries.",
      "notFoundCta": "Back to home"
    },
    "index": {
      "meta": {
        "title": "Relocation topics — compare 39 countries",
        "description": "Explore relocation one theme at a time: visas, taxes, cost of living, climate, healthcare, schools, safety and language, compared across 39 countries."
      },
      "sub": "Pick a dimension to see how 31 countries compare — published guides first, full coverage coming.",
      "title": "Browse by Topic"
    }
  },
  "wiki": {
    "atlas": "Atlas",
    "hub": {
      "metaTitle": "{{country}} relocation guide — visa, taxes, cost of living",
      "metaDescription": "A measured relocation guide to {{country}}: visa pathways, tax regimes, cost of living, climate, healthcare, schools, banking and more — for people seriously considering a move.",
      "tagline": "Relocation guide",
      "snapshotTitle": "At a glance",
      "snapshotIntro": "Seven dimensions, one country.",
      "topicsTitle": "Explore by topic",
      "topicsIntro": "Each chapter answers one question you actually have.",
      "annualTitle": "A year in {{country}}",
      "annualIntro": "Climate cadence, visa-renewal windows, cultural moments.",
      "neighborsTitle": "Compare with neighbours",
      "neighborsIntro": "Three same-region countries we score side-by-side.",
      "ctaQuiz": "Match against your priorities",
      "ctaChecklist": "Open the relocation checklist",
      "ctaMap": "Open the interactive map",
      "ctaGuide": "How to use the map →",
      "lastVerified": "Verified {{date}}",
      "comingSoonContent": "Long-form chapters land here as research finishes. The skeleton you see is the structure — content fills in country by country.",
      "draftBadge": "Draft",
      "publishedGuidesTitle": "Compare with other countries",
      "publishedGuidesIntro": "Full relocation guides for similar destinations."
    },
    "topic": {
      "visa": {
        "label": "Visas",
        "description": "Pathways, deadlines, paperwork"
      },
      "taxes": {
        "label": "Taxes",
        "description": "PIT rates, regimes, treaties"
      },
      "cost-of-living": {
        "label": "Cost of living",
        "description": "Rent, groceries, services"
      },
      "property": {
        "label": "Property",
        "description": "Buying, owning, paying tax on a home"
      },
      "climate": {
        "label": "Climate",
        "description": "Year-round temperatures, humidity, sun"
      },
      "healthcare": {
        "label": "Healthcare",
        "description": "Public, private, what you actually pay"
      },
      "education": {
        "label": "Education",
        "description": "Schools, kindergartens, expat options"
      },
      "banking": {
        "label": "Banking",
        "description": "Accounts, SWIFT, residency rules"
      },
      "transport": {
        "label": "Transport",
        "description": "Driving, car ownership, public transit"
      },
      "safety-community": {
        "label": "Safety & community",
        "description": "Crime, expat networks, support"
      },
      "language-culture": {
        "label": "Language & culture",
        "description": "What you need to learn, what you don't"
      },
      "infrastructure": {
        "label": "Infrastructure",
        "description": "Internet, utilities, daily logistics"
      }
    },
    "article": {
      "tocLabel": "Contents",
      "faqTitle": "Frequently asked",
      "relatedTopics": "Related chapters",
      "compareWithNeighbours": "Compare with neighbours",
      "backToHub": "Back to {{country}}",
      "verifiedAt": "Verified {{date}}",
      "byAuthor": "by {{author}}",
      "nextChapter": "Next chapter",
      "prevChapter": "Previous chapter",
      "allTopics": "All chapters"
    },
    "breadcrumb": {
      "atlas": "Atlas"
    },
    "notFound": {
      "title": "Page not found",
      "subtitle": "The country or topic you tried to open doesn't exist (yet).",
      "ctaAtlas": "Back to the atlas"
    }
  },
  "guide": {
    "label": "Field Guide",
    "metaDescription": "Lodestar Field Guide — practical articles on using the map, pins, shortlist, compare table, and AI helper to choose a place to live.",
    "rootTag": "Documentation",
    "sectionTag": "Section",
    "articleCount": "{{count}} article",
    "articleCount_other": "{{count}} articles",
    "emptySection": "Articles for this section are in progress.",
    "relatedArticles": "Related reading",
    "helpButton": {
      "aria": "Open the {{label}}",
      "title": "How to use the map",
      "openAll": "Open full guide →"
    },
    "section": {
      "gettingStarted": {
        "label": "Getting started",
        "description": "What the map is, what you can do without an account, and how to spend your first five minutes."
      },
      "useCases": {
        "label": "Use cases",
        "description": "Concrete jobs the map handles — real estate, climate search, family relocation, tax optimization, and more."
      },
      "workflows": {
        "label": "Workflows",
        "description": "End-to-end paths that chain several use cases into a single project — from quiz to decision, from listings to checklist."
      },
      "reference": {
        "label": "Reference",
        "description": "One article per surface: filters, layers, pins, shortlist, compare table, annotations, visit planner."
      },
      "faq": {
        "label": "FAQ",
        "description": "Quick answers to the questions that come up most often."
      }
    },
    "article": {
      "what-is-the-map": {
        "title": "What the map is for",
        "summary": "A working surface for narrowing 7,000+ locations down to a few real candidates."
      },
      "first-five-minutes": {
        "title": "Your first five minutes",
        "summary": "Land on the map, get a feel for the data, and leave with one shortlist."
      },
      "guest-vs-account": {
        "title": "Guest vs account",
        "summary": "What you can do logged out, what you gain by signing in."
      },
      "filters-panel": {
        "title": "Filters panel",
        "summary": "Climate, infrastructure, altitude, population — the dials that shrink the map."
      },
      "choropleth-layers": {
        "title": "Choropleth layers",
        "summary": "Reading the colour shading that maps a single metric across the territory."
      },
      "poi-layers": {
        "title": "POI layers",
        "summary": "Hospitals, stations, airports — the dots that ride on top of the base map."
      },
      "details-panel": {
        "title": "Details panel",
        "summary": "What opens on a click: twenty-two metrics, the source of each, and the buttons attached."
      },
      "pins": {
        "title": "Pins",
        "summary": "Personal bookmarks tied to coordinates — for listings, places you visited, or notes for later."
      },
      "bulk-import": {
        "title": "Bulk import",
        "summary": "Paste a list of addresses and the map geocodes and pins them in one pass."
      },
      "shortlist": {
        "title": "Shortlist",
        "summary": "Named collections of locations with per-location notes, sharable and exportable."
      },
      "annotations": {
        "title": "Annotations",
        "summary": "Sticky notes you place on the map after a trip or a phone call."
      },
      "compare-table": {
        "title": "Compare table",
        "summary": "Up to five locations side-by-side across twenty-two metrics — best and worst auto-highlighted."
      },
      "visit-planner": {
        "title": "Visit planner",
        "summary": "Turn a shortlist into a driving route of up to seven stops."
      },
      "ai-helper": {
        "title": "AI helper",
        "summary": "A small assistant in the corner of the map that reads your shortlist and answers questions."
      },
      "real-estate-listings": {
        "title": "Saving listings from a real-estate site",
        "summary": "From a tab full of property links to a map of candidates you can compare."
      },
      "climate-search": {
        "title": "Finding a place by climate",
        "summary": "Mild winters, dry summers, lots of sun — turn a preference into a filtered map."
      },
      "family-relocation": {
        "title": "Relocating with a family",
        "summary": "Schools, hospitals, airport proximity, and a community you can land in."
      },
      "remote-work": {
        "title": "Working remotely from elsewhere",
        "summary": "Internet speed plus cost plus an airport — the three things a remote worker needs."
      },
      "retirement": {
        "title": "Choosing a place for retirement",
        "summary": "Climate, healthcare, and tax regimes built for people on a pension."
      },
      "tax-optimization": {
        "title": "Comparing places on tax",
        "summary": "Income tax, property tax, and what a car costs to own — all on one screen."
      },
      "lifestyle-fit": {
        "title": "Lifestyle-driven search",
        "summary": "Mountains or coast, big city or small town — geography first, everything else after."
      },
      "visa-pathway": {
        "title": "Filtering by visa eligibility",
        "summary": "Filter the map down to places that actually want your passport."
      },
      "scouting-trip": {
        "title": "Planning a scouting trip",
        "summary": "Turn a shortlist into a route, take it offline, mark what you saw."
      },
      "final-decision": {
        "title": "Making the final call",
        "summary": "Three to five finalists, one table, one printout, one conversation."
      },
      "quiz-to-decision": {
        "title": "From the quiz to a decision",
        "summary": "The full path: take the quiz, narrow down on the map, compare, decide."
      },
      "listings-to-shortlist": {
        "title": "From a real-estate tab to a shortlist",
        "summary": "Paste the listings, let the map cluster them, surface the cluster worth visiting."
      },
      "scouting-to-checklist": {
        "title": "From a scouting trip to the moving checklist",
        "summary": "Annotate what you saw, narrow the shortlist, and roll into the relocation checklist."
      },
      "common-questions": {
        "title": "Common questions",
        "summary": "Quick answers about pins, shortlist, sync, and offline behaviour."
      }
    },
    "notFound": {
      "title": "Page not found",
      "subtitle": "This page does not exist in the Field Guide.",
      "cta": "Back to Field Guide"
    }
  },
  "consultantCta": {
    "eyebrow": "Human help",
    "button": "Talk to a consultant",
    "home": {
      "title": "Want a human to walk you through this?",
      "body": "Book a 30-min call with a relocation expert. Country choice, visa pathway, cost realism — talk it through."
    },
    "quiz_result": {
      "title": "Need a second opinion on these matches?",
      "body": "A relocation consultant can sanity-check the top-3 against your specific situation — taxes, family, work."
    },
    "recommendations": {
      "title": "Stuck between regions?",
      "body": "Talk to a consultant who knows Italy on the ground — they will short-list 2–3 for a visit plan."
    },
    "compare_empty": {
      "title": "Skip the comparison work?",
      "body": "A consultant can present a comparison table tailored to your budget, climate, and family in one session."
    },
    "compare_active": {
      "title": "Pick the right place to actually visit",
      "body": "A relocation consultant can sequence visits + flag the move-in details for the comuni you are comparing."
    },
    "next_steps": {
      "title": "Want a person owning these steps with you?",
      "body": "Book a consultant and we will turn this checklist into a dated plan."
    },
    "quiz_result_passport": {
      "title": "Passport friction? Talk to a specialist.",
      "body": "Immigration lawyers experienced with your passport type can navigate banking closures and visa restrictions."
    },
    "quiz_result_buyer": {
      "title": "Buying property? Get a real estate lawyer.",
      "body": "Property purchase abroad involves notary, tax registration, and transfer costs — get someone who has done it."
    },
    "country_hub": {
      "title": "Ready to talk to a country expert?",
      "body": "A relocation consultant can answer the specific questions no article can — your timeline, costs, and first steps."
    },
    "topic_visa": {
      "title": "Ready to apply? Talk to an immigration lawyer.",
      "body": "A lawyer can check your eligibility, prepare your application, and flag rejection risks before you submit."
    },
    "topic_taxes": {
      "title": "Tax rules are nuanced — let an accountant check yours.",
      "body": "A local tax accountant can map your income to the actual rates and tax treaties that apply to your situation."
    },
    "topic_cost-of-living": {
      "title": "Want a realistic budget for your situation?",
      "body": "A relocation consultant who lives there can give you honest numbers — not country averages."
    },
    "topic_property": {
      "title": "Buying or renting? Get a real estate lawyer.",
      "body": "Purchase process, notary, taxes, pitfalls — a lawyer protects you from the expensive surprises."
    },
    "topic_climate": {
      "title": "Need help picking the right region?",
      "body": "A consultant who lives there can tell you which areas actually match your climate and lifestyle preferences."
    },
    "topic_healthcare": {
      "title": "Health insurance is the part most expats get wrong.",
      "body": "A broker can find a plan that covers you properly — public registration, private add-ons, dental, family."
    },
    "topic_education": {
      "title": "Schools for kids? Talk to an education consultant.",
      "body": "International school availability, language tracks, and admission timing vary more than articles can capture."
    },
    "topic_banking": {
      "title": "Opening an account as a foreigner can take weeks.",
      "body": "A banking advisor knows which banks open accounts for your passport and exactly what documents to bring."
    },
    "topic_transport": {
      "title": "License exchange, car registration — get someone who has done it.",
      "body": "A local fixer can handle the paperwork and save you weeks of dealing with bureaucracy."
    },
    "topic_safety-community": {
      "title": "Pick the right neighbourhood with someone who lives there.",
      "body": "A relocation consultant gives you an honest safety and expat-community picture beyond what statistics show."
    },
    "topic_language-culture": {
      "title": "Find a tutor or cultural integration coach.",
      "body": "Language and cultural integration are the biggest predictors of long-term happiness abroad. Start early."
    },
    "topic_infrastructure": {
      "title": "Internet, utilities, paperwork — get a local fixer.",
      "body": "A relocation coordinator knows the provider landscape and can set up your apartment from day one."
    },
    "checklist_strategy": {
      "title": "Pick the right visa and tax stance with a pro.",
      "body": "An immigration lawyer and tax advisor can set the right strategy before you commit to a country."
    },
    "checklist_prep": {
      "title": "Documents, translations, notary — get someone to drive this.",
      "body": "A relocation consultant or immigration lawyer can manage the document pipeline so nothing gets missed."
    },
    "checklist_logistics": {
      "title": "Apostilles, shipping, school applications — full turnkey help.",
      "body": "A relocation coordinator can run your move logistics end-to-end."
    },
    "checklist_arrival": {
      "title": "First 2 weeks: account, registration, doctor.",
      "body": "A banking advisor and health insurance broker can get you set up before and after you arrive."
    },
    "checklist_settle": {
      "title": "Find an ongoing accountant for filings and residency.",
      "body": "A local tax accountant handles the ongoing compliance so you can focus on settling in."
    }
  },
  "career": {
    "holding": {
      "meta": {
        "title": "Career relocation — coming soon · Lodestar",
        "description": "We're building the career-relocation vector for lodestar.place. Job market analysis, visa pathways, real timelines, consultant access — for the published country list. Drop your email and we'll let you know when the first country goes live."
      },
      "eyebrow": "Coming soon",
      "title": "Career relocation, done consciously.",
      "intro": "Career relocation isn't just lifestyle. For many it's choosing where to build the next 5–10 years professionally — sponsored employment, visa for skilled migrants, salary that actually compounds. We're building this vector now, on top of the countries already covered in Lodestar.",
      "coming": {
        "title": "What's coming",
        "i1": "Country-by-country job-market analysis: market depth, English-at-work, typical senior salaries.",
        "i2": "Visa pathway comparison for skilled workers — what works for which passport.",
        "i3": "Realistic timelines from real candidates: how long does an offer actually take.",
        "i4": "Direct access to a relocation career consultant for your move."
      },
      "notify": {
        "title": "Be the first to know",
        "body": "We'll email you when the first country goes live. No spam, unsubscribe anytime.",
        "placeholder": "your@email.com",
        "cta": "Notify me",
        "submitting": "Sending…",
        "confirm": "Got it — we'll be in touch.",
        "micro": "We'll send one email when the feature ships. Nothing else."
      },
      "consultant": {
        "title": "Already in the process?",
        "body": "If you've already started your career relocation and want to talk to a consultant now — about a specific offer, sponsorship, or a country's hiring process — drop us a line.",
        "cta": "Talk to a consultant"
      }
    }
  },
  "report": {
    "pageTitle": "Your relocation report — Lodestar",
    "pageDescription": "Personal relocation report generated from your quiz answers.",
    "kicker": "Personal relocation report",
    "title": "Where you would thrive — and how to get there",
    "loading": "Assembling your report…",
    "decodeError": "We could not restore the quiz snapshot behind this report. Write to hello@lodestar.place — we will fix it manually.",
    "notFoundTitle": "Report not found",
    "notFoundText": "This link is invalid or expired. Check the link from your receipt email, or write to hello@lodestar.place.",
    "refundedTitle": "This purchase was refunded",
    "refundedText": "The report link is no longer active. If this is a mistake, contact hello@lodestar.place.",
    "pendingTitle": "Finishing your payment…",
    "pendingText": "The payment provider is confirming your order — this usually takes a few seconds. The page refreshes automatically.",
    "pendingStuckText": "The confirmation is taking longer than usual. Keep this link — the report unlocks the moment the payment settles. If nothing changes within an hour, write to hello@lodestar.place.",
    "toolbarHint": "This link is yours — bookmark it. The report re-renders from live data on every visit.",
    "downloadPdf": "Save as PDF",
    "compareTitle": "Your top three, side by side",
    "compareDim": "Dimension",
    "methodology": "Scores combine your quiz answers with per-country datasets (visa programs, tax regimes, property costs, car ownership, passport treatment) maintained by the Lodestar editorial team. Percentages are relative fit for YOUR answers, not absolute country grades.",
    "disclaimer": "This report is guidance, not legal, tax, or immigration advice. Rules change; verify deadlines and amounts with official sources or a licensed professional before acting.",
    "band": {
      "excellent": "Excellent fit",
      "strong": "Strong fit",
      "fair": "Fair fit",
      "weak": "Weak fit"
    },
    "whyFits": "Why it fits you",
    "watchOut": "Watch out for",
    "dimsTitle": "Fit by dimension",
    "visaPath": "Your strongest visa path",
    "visaIncomeFloor": "income floor €{{amount}}/mo",
    "taxes": "Income tax",
    "taxPit": "Personal income tax up to",
    "taxRegime": "Special regime:",
    "property": "Property taxes",
    "car": "Car ownership",
    "perMonth": "mo",
    "passportNote": "Passport-specific note",
    "planTitle": "Your first 12 months — the phases",
    "planStartsWith": "starts with",
    "guideLink": "Read the full {{name}} guide",
    "checklistLink": "Open the interactive checklist",
    "upsell": {
      "title": "Turn this result into your personal relocation report",
      "subtitle": "A deep-dive document on your top-3 countries, built from your exact answers. A relocation consultant charges €100+ for one hour; this is the prepared version of that hour.",
      "bullet1": "Visa pathways that actually fit your passport — with income floors",
      "bullet2": "Tax picture for your situation: rates, special regimes, treaty notes",
      "bullet3": "First-year budget: housing taxes, car costs, the numbers behind the score",
      "bullet4": "A 12-month action plan per country + a permanent link, PDF-ready",
      "cta": "Get my report — €29",
      "loading": "Opening checkout…",
      "note": "One-time payment · instant access · permanent link",
      "error": "Checkout failed — try again.",
      "previewCta": "Preview your report — free"
    },
    "locked": {
      "title": "Unlock the full report",
      "subtitle": "You just saw the shape of your #1. The numbers that turn “where” into “how” — and the other two countries — are in the full document. A relocation consultant charges €100+ for one hour; this is the prepared version of that hour.",
      "factHint": "Income floor · tax · budget · plan — in your report",
      "unlock1": "All three countries in full — not just your #1",
      "unlock2": "Exact visa pathway + monthly income floor for your passport",
      "unlock3": "Tax regime, property & car costs — the first-year budget in numbers",
      "unlock4": "A 12-month action plan per country + side-by-side comparison",
      "unlock5": "A permanent link, PDF-ready for printing"
    },
    "preview": {
      "pageTitle": "Preview your relocation report — Lodestar",
      "pageDescription": "A free preview of your personal relocation report, built from your quiz answers.",
      "banner": "Free preview · this is your #1 — unlock all three below"
    },
    "seoCta": {
      "title": "Is {{country}} actually your best move?",
      "subtitle": "Take the 2-minute quiz to see how {{country}} ranks against the rest for your situation — then get a personal report on your top-3 with visa pathways, the tax picture and a first-year budget.",
      "cta": "Find my best-fit countries →",
      "note": "Free quiz · report €29 · instant access"
    }
  },
  "refund": {
    "back": "← Back",
    "title": "Refund policy",
    "updated": "Last updated: {{date}}",
    "intro": "The Personal Relocation Report is a digital product. It is generated from your own quiz answers and delivered instantly as a private link the moment your payment is confirmed.",
    "s1": {
      "title": "1. Instant delivery and your consent",
      "body": "By buying the report you ask for it to be delivered immediately and you agree to that delivery. Under EU consumer law, the 14-day right of withdrawal for digital content ends once delivery has begun — for this product, that is the moment your report link is issued."
    },
    "s2": {
      "title": "2. When we refund in full",
      "lead": "We will refund you in full if:",
      "i1": "you were charged but never received access to your report (a technical failure on our side);",
      "i2": "you were charged more than once for the same order, or charged by accident;",
      "i3": "the report failed to generate, was empty, or was corrupted and we could not fix it.",
      "how": "Email us at hello@lodestar.place within 14 days of purchase with your order number, and we will sort it out."
    },
    "s3": {
      "title": "3. When we cannot refund",
      "body": "Because the report is digital content that cannot be returned, we cannot offer a \"changed my mind\" refund once the report has been delivered and opened. If something is wrong with the report itself, tell us — see section 2."
    },
    "s4": {
      "title": "4. How refunds are processed",
      "body": "Lemon Squeezy is our Merchant of Record. Approved refunds are returned to your original payment method through Lemon Squeezy, usually within 5–10 business days."
    },
    "s5": {
      "title": "5. Contact",
      "before": "Questions about a payment or refund? Email ",
      "email": "hello@lodestar.place",
      "after": " and we will reply within a couple of business days."
    }
  },
  "reportLanding": {
    "metaTitle": "Personal Relocation Report — Lodestar",
    "metaDescription": "A €29 deep-dive report on your top-3 destinations, generated from your own quiz answers: visa pathways, taxes, first-year budget, and a 12-month plan.",
    "eyebrow": "Personal relocation report",
    "h1": "Your move, mapped — country by country",
    "sub": "A deep-dive report on the three countries that fit you best, generated from your own quiz answers. Every number behind the decision, in one document you keep forever.",
    "price": "€29 · one-time · instant access",
    "includesTitle": "What's inside",
    "b1": "Visa pathways that actually fit your passport — with the income floors for each",
    "b2": "The tax picture for your situation: rates, special regimes, treaty notes",
    "b3": "First-year budget: property taxes, car costs, the numbers behind every score",
    "b4": "A 12-month action plan per country, plus a permanent link that is PDF-ready",
    "howTitle": "How it works",
    "h1step": "Take the 2-minute quiz",
    "h1body": "Answer questions about climate, budget, passport, family and priorities.",
    "h2step": "See your ranked countries",
    "h2body": "We score 31 countries on your answers and show your top matches — free.",
    "h3step": "Unlock the full report",
    "h3body": "Turn your result into the deep-dive document on your top-3, for €29.",
    "premade": "The report is generated automatically the instant you buy — there is no waiting, no call, no human in the loop. It is a finished digital product, not a consultation.",
    "cta": "Take the 2-minute quiz →",
    "policiesLead": "By purchasing you agree to our",
    "terms": "Terms",
    "refund": "Refund policy",
    "privacy": "Privacy"
  },
  "annotations": {
    "add": "Add note",
    "cancelHint": "Click again to cancel",
    "delete": "Delete",
    "deleteConfirm": "Delete this note?",
    "placeholder": "Your note…",
    "placing": "Click map to place…",
    "save": "Save",
    "saving": "Saving…",
    "startHint": "Click anywhere on the map to add a note",
    "toolbar": "Annotations"
  },
  "close": "Close",
  "common": {
    "cancel": "Cancel",
    "close": "Close"
  },
  "district": {
    "kind": {
      "cc": "Comunità comprensoriale",
      "sll": "Local labour system (SLL)",
      "ca": "Comarca agraria",
      "concelho": "Municipality"
    }
  },
  "partners": {
    "dpa": {
      "s1": {
        "body": "Lodestar acts as Data Controller of the end-user data collected through the platform until the moment a lead is accepted by Partner. After acceptance, Partner becomes an independent Data Controller of the data shared with it, processing that data for its own consulting purposes.",
        "title": "1. Roles"
      },
      "s10": {
        "body": "Lodestar may, on reasonable notice and not more than once per year, request information about Partner's compliance with this DPA. On-site audits may be conducted at Lodestar's expense where independent certifications (ISO 27001 or equivalent) are not available.",
        "title": "10. Audit"
      },
      "s2": {
        "body": "Identification: full name, email, optional phone. Profile signal: target country, service category, timeline, language. No special categories of data (GDPR Art. 9) are processed.",
        "title": "2. Categories of data"
      },
      "s3": {
        "body": "Processing is based on the explicit consent of the end user (GDPR Art. 6(1)(a)) and on the performance of the introduction service requested by the user (Art. 6(1)(b)). End users may withdraw consent at any time, after which Partner will cease processing the related data unless an independent lawful basis exists.",
        "title": "3. Purpose and lawful basis"
      },
      "s4": {
        "body": "Lodestar uses the following sub-processors: Cloudflare (D1, R2, Workers, hosting), Resend (transactional email), Sentry (error monitoring). Partner will maintain an equivalent list of its own sub-processors and notify Lodestar of changes that affect Lodestar-originated data.",
        "title": "4. Sub-processors"
      },
      "s5": {
        "body": "When personal data is transferred outside the European Economic Area, transfers are protected by Standard Contractual Clauses (SCCs) and supplementary measures as required.",
        "title": "5. International transfers"
      },
      "s6": {
        "body": "Both parties will maintain reasonable technical and organisational measures including: encryption in transit (TLS) and at rest, access on a need-to-know basis, multi-factor authentication for administrative access, regular backups, and an incident response process. IP addresses are not stored by Lodestar (cf-ipcountry only).",
        "title": "6. Security measures"
      },
      "s7": {
        "body": "Partner will assist Lodestar in responding to data subject requests (access, rectification, erasure, portability, objection) within statutory deadlines. Partner will not respond directly to a Lodestar-originated end user without first notifying Lodestar.",
        "title": "7. Data subject rights"
      },
      "s8": {
        "body": "Each party will notify the other without undue delay and in any event within 72 hours of becoming aware of a personal data breach involving data originating from the other party.",
        "title": "8. Breach notification"
      },
      "s9": {
        "body": "Partner will retain end-user data only as long as required to deliver the requested service and to satisfy legal obligations (typically 24 months from completion). On termination of the Partner Agreement, Partner will delete or return all Lodestar-originated personal data within 30 days, except where retention is required by law.",
        "title": "9. Retention and deletion"
      }
    },
    "terms": {
      "s1": {
        "body": "This agreement is between Lodestar (the operating entity behind lodestar.place; jurisdiction to be confirmed at company registration) and the consultant or firm that has been approved as a Partner.",
        "title": "1. Parties"
      },
      "s2": {
        "body": "Lodestar will route qualified relocation enquiries to Partner based on country, service category, and language coverage. Partner is responsible for delivering the requested consulting services to end users on its own terms.",
        "title": "2. Scope"
      },
      "s3": {
        "body": "Partner pays Lodestar a per-acceptance fee disclosed at the time a lead is offered. No subscription is required during the MVP phase. Payments are billed via Stripe Connect once enabled; founding partners receive 90 days free or the first 20 accepted leads, whichever comes first.",
        "title": "3. Fees and payment"
      },
      "s4": {
        "body": "Partner will treat all end-user data received through the platform as strictly confidential and will not use it for any purpose other than delivering the requested service. Onward sharing with sub-processors requires written notice to Lodestar.",
        "title": "4. Confidentiality"
      },
      "s5": {
        "body": "Partner agrees not to solicit end users introduced via Lodestar for services that bypass the platform during the active engagement and for 12 months after.",
        "title": "5. Anti-circumvention"
      },
      "s6": {
        "body": "Both parties will comply with applicable data protection law (GDPR or equivalent). A separate Data Processing Agreement governs the technical and organisational measures.",
        "title": "6. Data protection"
      },
      "s7": {
        "body": "Either party may terminate this agreement at any time with 30 days written notice. Lodestar may suspend or terminate immediately for material breach (fraud, abuse of user data, repeated SLA violations).",
        "title": "7. Termination"
      },
      "s8": {
        "body": "Lodestar provides the lead-matching service \"as is\" and is not a party to the consulting engagement between Partner and the end user. Lodestar is not liable for the outcome of the consulting services.",
        "title": "8. Liability"
      },
      "s9": {
        "body": "This agreement is governed by the law of the jurisdiction where Lodestar is registered. Disputes will be resolved in the competent courts of that jurisdiction.",
        "title": "9. Governing law"
      }
    }
  }
}
